import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { invoicesApi } from '../api/invoices';
import { purchasesApi } from '../api/purchases';
import { expensesApi } from '../api/expenses';
import { partiesApi } from '../api/parties';
import { companyApi } from '../api/company';
import { productsApi } from '../api/products';
import { gstBreakdown, parseGstr2aJson } from '@coreledger/business-logic';
import { API_BASE } from '../api/client';
import { FeatureGate } from '../components/FeatureGate';
import { subscriptionApi } from '../api/subscription';
import { reportsApi } from '../api/reports';
import { loanTransactionsApi } from '../api/loanTransactions';
import { loanAccountsApi } from '../api/loanAccounts';
import { api as apiClient } from '../api/client';
import { manufacturingApi, type CostVarianceDto } from '../api/manufacturing';
import { gstrFilingApi, type Gstr1ValidationResult } from '../api/gstrFiling';

interface Gstr2aRow {
  supplierGstin: string; supplierName: string; invoiceNumber: string;
  invoiceDate: string; taxableValue: number; igst: number; cgst: number;
  sgst: number; totalTax: number;
  matchStatus: 'matched' | 'in_2a_only' | 'in_local_only';
  localInvoiceNumber?: string; taxDiff?: number;
}

interface LedgerEntry {
  date: string; type: string; description: string;
  invoiceNumber?: string; debit: number; credit: number; balance: number;
}

function toGstr2aRows(raw: unknown): Gstr2aRow[] {
  const data = ((raw as Record<string, unknown>)?.data ?? raw) as Record<string, unknown>;
  return parseGstr2aJson(data).map(e => ({
    supplierGstin: e.supplierGstin,
    supplierName: e.supplierName ?? e.supplierGstin,
    invoiceNumber: e.invoiceNumber,
    invoiceDate: e.invoiceDate ?? '',
    taxableValue: e.taxableValue,
    igst: e.igst, cgst: e.cgst, sgst: e.sgst,
    totalTax: e.igst + e.cgst + e.sgst,
    matchStatus: 'in_2a_only' as const,
  }));
}

const FY_LIST = ['2026-27', '2025-26', '2024-25', '2023-24'];

// Groups the page's 21 individual reports into a handful of tabs, and each
// tab into a menu of report tiles, so opening Reports shows one thing at a
// time — first which category, then which report — instead of everything
// stacked on one long scroll. The report cards/logic below are all
// unchanged, each is just wrapped in an `activeTab === '<id>' &&
// activeReport === '<id>'` check at its existing spot in the file.
type ReportCategory = 'gst' | 'salesPurchases' | 'financial' | 'moneyInOut' | 'tax' | 'operations';
interface ReportTile { id: string; title: string; desc: string; }
const REPORT_CATEGORIES: { id: ReportCategory; label: string; blurb: string; reports: ReportTile[] }[] = [
  {
    id: 'gst', label: 'GST & Filing',
    blurb: 'Everything you file with the GST portal — returns, reconciliation, and the HSN summary that backs them.',
    reports: [
      { id: 'gstr1', title: 'GSTR-1', desc: 'Outward supplies for the period, split into B2B and B2C.' },
      { id: 'gstr3b', title: 'GSTR-3B', desc: 'Monthly summary return — tax payable at a glance.' },
      { id: 'gstr2a', title: 'GSTR-2A Reconciliation', desc: 'Match your purchases against what suppliers filed.' },
      { id: 'hsnSummary', title: 'HSN Summary', desc: 'Table 12 — taxable value grouped by HSN code.' },
      { id: 'rcm', title: 'RCM Supplies Report', desc: 'Supplies where the recipient pays the tax, not you.' },
    ],
  },
  {
    id: 'salesPurchases', label: 'Sales & Purchases',
    blurb: 'Every invoice, note, and quote you’ve raised or received, in one register per type.',
    reports: [
      { id: 'salesRegister', title: 'Sales Register', desc: 'Every sales invoice for the year, one row each.' },
      { id: 'purchaseRegister', title: 'Purchase Register', desc: 'Every purchase invoice for the year.' },
      { id: 'creditNoteRegister', title: 'Credit Note Register', desc: 'Credit notes issued against sales.' },
      { id: 'debitNoteRegister', title: 'Debit Note Register', desc: 'Debit notes — supplier returns and corrections.' },
      { id: 'quotationTracker', title: 'Quotation Tracker', desc: 'Open, won, and lost quotes with conversion rate.' },
    ],
  },
  {
    id: 'moneyInOut', label: 'Money In / Out',
    blurb: 'Who owes you, who you owe, and the running ledger behind both.',
    reports: [
      { id: 'receivablesAgeing', title: 'Receivables Ageing', desc: 'Outstanding customer balances, by how overdue.' },
      { id: 'payablesAgeing', title: 'Payables Ageing', desc: 'Outstanding supplier balances, by how overdue.' },
      { id: 'partyLedger', title: 'Party Ledger', desc: 'Full transaction history for one customer or supplier.' },
      { id: 'cashLedger', title: 'Cash Ledger', desc: 'Cash-mode receipts and payments for the year.' },
    ],
  },
  {
    id: 'financial', label: 'Financial Statements',
    blurb: 'The two statements your accountant actually asks for.',
    reports: [
      { id: 'pnl', title: 'Profit & Loss Statement', desc: 'Revenue, cost, and net profit for the year.' },
      { id: 'balanceSheet', title: 'Balance Sheet', desc: 'Assets, liabilities, and equity as of today.' },
    ],
  },
  {
    id: 'tax', label: 'Tax (TDS)',
    blurb: 'Tax deducted at source — what was withheld, and what’s payable.',
    reports: [
      { id: 'tdsTcs', title: 'TDS / TCS Deductions', desc: 'Tax deducted on invoices, by section.' },
      { id: 'tdsPayable', title: 'TDS Payable Report', desc: 'What’s due to be deposited, section-wise.' },
    ],
  },
  {
    id: 'operations', label: 'Operations',
    blurb: 'Stock, production cost, and bank reconciliation — the operational reports.',
    reports: [
      { id: 'stockMovement', title: 'Stock Movement Report', desc: 'Opening, purchased, sold, and closing quantity.' },
      { id: 'productionCostVariance', title: 'Production Cost Variance', desc: 'Planned vs. actual cost on manufacturing orders.' },
    ],
  },
];

const fmt = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n: number) => '₹' + Math.abs(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);

function fyToDateRange(fy: string): { from: string; to: string } {
  const parts = fy.split('-');
  const y1 = parseInt(parts[0]);
  const y2s = parts[1].length === 2 ? '20' + parts[1] : parts[1];
  return { from: `${y1}-04-01`, to: `${y2s}-03-31` };
}

// The day immediately before a YYYY-MM-DD date — used to ask "what was our
// stock worth right before this period started" (the P&L's Opening Stock
// Value), i.e. the closing position of whatever came before.
function dayBefore(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function printHtml(html: string, title: string) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
    body{font-family:Arial,sans-serif;font-size:11px;color:#1e293b;padding:20px}
    h1{font-size:16px;margin:0 0 4px}.sub{color:#64748b;font-size:10px;margin-bottom:16px}
    table{width:100%;border-collapse:collapse}
    th{background:#f1f5f9;padding:6px 8px;text-align:left;font-size:10px;border-bottom:2px solid #e2e8f0}
    td{padding:5px 8px;border-bottom:1px solid #f1f5f9;font-size:10px}
    tfoot td{background:#1e293b;color:white;font-weight:700;padding:7px 8px}
    .r{text-align:right}.b{font-weight:700}.red{color:#dc2626}.green{color:#16a34a}.orange{color:#d97706}
  </style></head><body>${html}</body></html>`);
  w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
}

function ReportCard({ title, actions, children }: { title: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>{title}</span>
        {actions && <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>{actions}</div>}
      </div>
      <div style={{ overflowX: 'auto' }}>{children}</div>
    </div>
  );
}

function EmptyRow({ cols, msg }: { cols: number; msg: string }) {
  return <tr><td colSpan={cols} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem', fontSize: '0.875rem' }}>{msg}</td></tr>;
}

export default function ReportsPage() {
  const [fy, setFy] = useState('2026-27');
  const [activeTab, setActiveTab] = useState<ReportCategory>('gst');
  // null = showing the category's report menu (tiles); a report id = that
  // one report is open, replacing the tiles.
  const [activeReport, setActiveReport] = useState<string | null>(null);
  const [stockCategoryFilter, setStockCategoryFilter] = useState<'' | 'raw_material' | 'finished_good'>('');
  const [dateMode, setDateMode] = useState<'fy' | 'custom'>('fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // partyId is either a UUID (formal party) or "name:SomeName" (name-only party from invoices)
  const [partyId, setPartyId] = useState('');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const gstr2aRef = useRef<HTMLInputElement>(null);
  const [gstr2aRows, setGstr2aRows] = useState<Gstr2aRow[] | null>(null);
  const [gstr2aError, setGstr2aError] = useState<string | null>(null);

  const [bsAsOf, setBsAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [gstr1FilingModal, setGstr1FilingModal] = useState(false);
  const [gstr1ValidationResult, setGstr1ValidationResult] = useState<Gstr1ValidationResult | null>(null);
  const [gstr1FilingOtp, setGstr1FilingOtp] = useState('');
  const [gstr1FilingMsg, setGstr1FilingMsg] = useState<string | null>(null);
  const [gstr1FilingLoading, setGstr1FilingLoading] = useState(false);
  const [gstr2aSyncState, setGstr2aSyncState] = useState<'idle' | 'otp_sent' | 'syncing'>('idle');
  const [gstr2aSyncTxnId, setGstr2aSyncTxnId] = useState('');
  const [gstr2aSyncOtp, setGstr2aSyncOtp] = useState('');
  const [gstr2aSyncMsg, setGstr2aSyncMsg] = useState<string | null>(null);
  const { data: balanceSheet } = useQuery({
    queryKey: ['balance-sheet', bsAsOf],
    queryFn: () => reportsApi.balanceSheet(bsAsOf),
    staleTime: 60_000,
  });

  const { data: costVariance } = useQuery({
    queryKey: ['manufacturing-cost-variance'],
    queryFn: manufacturingApi.getCostVariance,
    staleTime: 60_000,
  });

  const { data: sub } = useQuery({ queryKey: ['subscription'], queryFn: subscriptionApi.get, staleTime: 60_000 });
  function guardExport(): boolean {
    if (sub && (sub.plan === 'TRIAL' || sub.isExpired)) {
      alert('GST report exports are not available on the Free Trial plan.\nUpgrade your plan to download CSV / JSON exports.');
      return false;
    }
    return true;
  }

  const { data: invoicesResult } = useQuery({ queryKey: ['invoices'], queryFn: () => invoicesApi.list({}, 1, 200), staleTime: 30 * 1000 });
  const invoices = invoicesResult?.data ?? [];
  const { data: purchasesResult } = useQuery({ queryKey: ['purchases'], queryFn: () => purchasesApi.list(1, 200), staleTime: 30 * 1000 });
  const purchases = purchasesResult?.data ?? [];
  const { data: expensesResult } = useQuery({ queryKey: ['expenses'], queryFn: () => expensesApi.list(1, 200), staleTime: 30 * 1000 });
  const expenses = expensesResult?.data ?? [];
  // Only needed for the TDS Payable report's third source (loan interest
  // TDS) — see CLAUDE.md's Loans & Capital note. loanAccounts is a small
  // per-tenant list (a lender/loan relationship, not a transaction), used
  // here purely to resolve each transaction's lenderName for display.
  const { data: loanTransactionsResult } = useQuery({ queryKey: ['loan-transactions'], queryFn: () => loanTransactionsApi.list(1, 200), staleTime: 30 * 1000 });
  const loanTransactions = loanTransactionsResult?.data ?? [];
  const { data: loanAccountsResult } = useQuery({ queryKey: ['loan-accounts'], queryFn: () => loanAccountsApi.list(1, 200), staleTime: 30 * 1000 });
  const loanAccountsById = new Map((loanAccountsResult?.data ?? []).map(a => [a.id, a]));
  const { data: partiesResult } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list(undefined, 1, 200), staleTime: 30 * 1000 });
  const parties = partiesResult?.data ?? [];
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: companyApi.get, staleTime: 30 * 1000 });
  const { data: productsResult } = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list(undefined, 1, 200), staleTime: 30 * 1000 });
  const products = productsResult?.data ?? [];
  const { data: stockAdjustments = [] } = useQuery({ queryKey: ['stock-adjustments'], queryFn: () => productsApi.getAllAdjustments(), staleTime: 30 * 1000 });
  const { data: manufacturingOrders = [] } = useQuery({ queryKey: ['manufacturing-orders-report'], queryFn: () => manufacturingApi.listOrders(), staleTime: 30 * 1000 });

  const isNameOnly = partyId.startsWith('name:');
  const nameOnlyValue = isNameOnly ? partyId.slice(5) : '';
  const { data: _ledgerRaw, isLoading: ledgerLoading } = useQuery({
    queryKey: ['party-ledger', partyId],
    queryFn: () => isNameOnly ? partiesApi.ledgerByName(nameOnlyValue) : partiesApi.ledger(partyId!),
    enabled: !!partyId,
    staleTime: 30 * 1000,
  });
  const ledgerData: LedgerEntry[] = _ledgerRaw?.entries ?? [];

  // Names from invoices that don't have a formal Party record
  const partyNamesInDb = new Set(parties.map(p => p.name.toLowerCase()));
  const invoicePartyNames = Array.from(new Set([
    ...invoices.map(i => i.partyName).filter((n): n is string => !!n && !partyNamesInDb.has(n.toLowerCase())),
    ...purchases.map(p => p.partyName).filter((n): n is string => !!n && !partyNamesInDb.has(n.toLowerCase())),
  ])).sort();

  const { from: fyFrom, to: fyTo } = fyToDateRange(fy);
  const from = (dateMode === 'custom' && customFrom) ? customFrom : fyFrom;
  const to   = (dateMode === 'custom' && customTo)   ? customTo   : fyTo;

  // Opening/Closing Stock Value for the P&L's COGS formula (Opening +
  // Purchases − Closing) and the Stock Movement Report's own Closing
  // Value/Avg Cost columns — both now read from the same shared
  // server-side FIFO valuation (ProductsService.computeStockValuation())
  // Balance Sheet's Stock Value already calls, instead of each report
  // computing (or, for the P&L, simply never computing) its own figure.
  // See CLAUDE.md's stock-valuation note.
  const { data: openingStockValuation } = useQuery({
    queryKey: ['stock-valuation', dayBefore(from)],
    queryFn: () => productsApi.stockValuation(dayBefore(from)),
    staleTime: 30 * 1000,
  });
  const { data: closingStockValuation } = useQuery({
    queryKey: ['stock-valuation', to],
    queryFn: () => productsApi.stockValuation(to),
    staleTime: 30 * 1000,
  });
  const openingStockValue = openingStockValuation?.totalValue ?? 0;
  const closingStockValue = closingStockValuation?.totalValue ?? 0;
  const closingValueByProduct = new Map((closingStockValuation?.byProduct ?? []).map(p => [p.productId, p]));

  // HSN Summary and Cash Ledger — both now read from the real server
  // endpoints (GET /reports/hsn-summary, GET /reports/cash-ledger) instead
  // of each being computed independently client-side. Same "two
  // uncoordinated implementations of the same figure" shape that caused the
  // Balance Sheet/P&L/Stock Movement discrepancies fixed in the prior
  // session — the server versions were fully built and tested but never
  // actually called by the app.
  const { data: hsnSummaryData = [] } = useQuery({
    queryKey: ['hsn-summary', from, to],
    queryFn: () => reportsApi.hsnSummary(from, to),
    staleTime: 30 * 1000,
  });
  const { data: cashLedgerData } = useQuery({
    queryKey: ['cash-ledger', from, to],
    queryFn: () => reportsApi.cashLedger(from, to),
    staleTime: 30 * 1000,
  });
  // Real interest income for the P&L's "Other Income" line — previously
  // nonexistent in the P&L entirely, since a bank entry tagged "Interest
  // Income" via Bank Reconciliation's Suggested Ledger used to write
  // nothing but a cosmetic label (see CLAUDE.md's Loan/Capital note).
  const { data: interestData } = useQuery({
    queryKey: ['interest-summary', from, to],
    queryFn: () => reportsApi.interestSummary(from, to),
    staleTime: 30 * 1000,
  });

  async function run(key: string, fn: () => void | Promise<void>) {
    setLoading(key); setError(null);
    try { await fn(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
    finally { setLoading(null); }
  }
  const Btn = ({ k, label, onClick }: { k: string; label: string; onClick: () => void | Promise<void> }) => (
    <button className="btn btn-ghost btn-sm" onClick={() => run(k, onClick)} disabled={loading === k} style={{ fontSize: '0.78rem' }}>
      {loading === k ? <span className="spinner" style={{ width: 12, height: 12 }} /> : label}
    </button>
  );

  // ── Derived data ─────────────────────────────────────────────────────────

  const fyInvoices = invoices.filter(i => i.date >= from && i.date <= to && i.status !== 'draft');
  const salesInvoices = fyInvoices.filter(i => i.docType !== 'lorry_receipt');
  // Quotations are unconfirmed, non-binding drafts — never a real outward supply, so they must
  // never feed a compliance figure (GSTR-1, GSTR-3B, HSN Summary). Sales/Credit Note/Debit Note
  // Registers deliberately keep using salesInvoices as-is (an activity register reasonably
  // includes quotations); this narrower array is only for actual tax filings.
  const gstEligibleInvoices = salesInvoices.filter(i => i.docType !== 'quotation');
  const fyPurchases = purchases.filter(p => p.date >= from && p.date <= to && p.status === 'confirmed');
  const fyExpenses = expenses.filter(e => e.date >= from && e.date <= to);
  const fyLoanTransactions = loanTransactions.filter(t => t.date >= from && t.date <= to);

  // GSTR-1 data — RCM supplies split from regular B2B (Table 4A vs 4B)
  const isCdnr = (i: typeof salesInvoices[0]) => i.docType === 'credit_note' || i.docType === 'debit_note';
  const gstr1b2b    = gstEligibleInvoices.filter(i => i.partyGstin && !isCdnr(i) && !i.isRcm);
  const gstr1b2bRcm = gstEligibleInvoices.filter(i => i.partyGstin && !isCdnr(i) &&  i.isRcm);
  const gstr1b2c    = gstEligibleInvoices.filter(i => !i.partyGstin && !isCdnr(i) && !i.isRcm);
  const gstr1cdnr   = gstEligibleInvoices.filter(i => isCdnr(i));

  // GSTR-3B computation
  const gstr3b = (() => {
    const period  = gstEligibleInvoices;
    const regular = period.filter(i => !isCdnr(i) && !i.isRcm);   // normal outward (supplier collects tax)
    const rcmOut  = period.filter(i => !isCdnr(i) &&  i.isRcm);   // RCM outward (recipient pays tax — no output tax for supplier)
    const cdnr    = period.filter(i => isCdnr(i));

    // Regular outward tax (3.1a) — excluding RCM
    const outIgst = regular.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).igst, 0) - cdnr.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).igst, 0);
    const outCgst = regular.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).cgst, 0) - cdnr.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).cgst, 0);
    const outSgst = regular.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).sgst, 0) - cdnr.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).sgst, 0);
    const outTaxable = regular.reduce((s, i) => s + i.subtotal, 0) - cdnr.reduce((s, i) => s + i.subtotal, 0);

    // RCM outward (3.1d) — taxable value only, NO output tax from supplier
    const rcmTaxable = rcmOut.reduce((s, i) => s + i.subtotal, 0);

    // ITC from purchases — include draft + confirmed (exclude cancelled only)
    // Debit notes (purchase returns) REVERSE the ITC already claimed
    const fyPurch = purchases.filter(p => p.date >= from && p.date <= to && p.status !== 'cancelled');
    const regularPurch = fyPurch.filter(p => p.docType !== 'debit_note');
    const debitNotes   = fyPurch.filter(p => p.docType === 'debit_note');
    const purchItcIgst = regularPurch.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).igst, 0)
                       - debitNotes.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).igst, 0);
    const purchItcCgst = regularPurch.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).cgst, 0)
                       - debitNotes.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).cgst, 0);
    const purchItcSgst = regularPurch.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).sgst, 0)
                       - debitNotes.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).sgst, 0);
    const dnRevIgst = debitNotes.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).igst, 0);
    const dnRevCgst = debitNotes.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).cgst, 0);
    const dnRevSgst = debitNotes.reduce((s, p) => s + gstBreakdown(p.taxType, p.taxTotal).sgst, 0);
    // ITC from expenses — only where GST rate is explicitly set > 0
    const fyExp = expenses.filter(e => e.date >= from && e.date <= to && (e.gstRate ?? 0) > 0);
    const expItcIgst = fyExp.filter(e => e.gstTaxType === 'igst').reduce((s, e) => s + e.amount * (e.gstRate ?? 0) / 100, 0);
    const expItcCgstSgst = fyExp.filter(e => e.gstTaxType !== 'igst').reduce((s, e) => s + e.amount * (e.gstRate ?? 0) / 100 / 2, 0);
    const itcIgst = purchItcIgst + expItcIgst;
    const itcCgst = purchItcCgst + expItcCgstSgst;
    const itcSgst = purchItcSgst + expItcCgstSgst;
    const nilExempt = salesInvoices.filter(i => i.docType === 'bill_of_supply').reduce((s, i) => s + i.total, 0);

    // ── Section 49 CGST Act — ITC utilization order ──────────────────────────
    // Step 1: IGST ITC → IGST liability first
    let igstLiab = Math.max(0, outIgst);
    let cgstLiab = Math.max(0, outCgst);
    let sgstLiab = Math.max(0, outSgst);
    let balIgst = Math.max(0, itcIgst);
    let balCgst = Math.max(0, itcCgst);
    let balSgst = Math.max(0, itcSgst);

    const use = (avail: number, liab: number) => [Math.min(avail, liab), Math.max(0, avail - Math.min(avail, liab)), Math.max(0, liab - Math.min(avail, liab))] as const;

    // Step 1: IGST ITC vs IGST
    [, balIgst, igstLiab] = use(balIgst, igstLiab);
    // Step 2: CGST ITC vs CGST
    [, balCgst, cgstLiab] = use(balCgst, cgstLiab);
    // Step 3: SGST ITC vs SGST
    [, balSgst, sgstLiab] = use(balSgst, sgstLiab);
    // Step 4: Remaining IGST ITC vs CGST, then SGST
    [, balIgst, cgstLiab] = use(balIgst, cgstLiab);
    [, balIgst, sgstLiab] = use(balIgst, sgstLiab);
    // Step 5: Remaining CGST ITC vs IGST (cannot touch SGST)
    [, balCgst, igstLiab] = use(balCgst, igstLiab);
    // Step 6: Remaining SGST ITC vs IGST (cannot touch CGST)
    [, balSgst, igstLiab] = use(balSgst, igstLiab);

    return {
      outTaxable, outIgst, outCgst, outSgst, outTotal: outIgst + outCgst + outSgst,
      rcmTaxable, rcmCount: rcmOut.length,
      itcIgst, itcCgst, itcSgst, itcTotal: itcIgst + itcCgst + itcSgst,
      nilExempt,
      netIgst: igstLiab, netCgst: cgstLiab, netSgst: sgstLiab,
      carryIgst: balIgst, carryCgst: balCgst, carrySgst: balSgst,
      dnRevIgst, dnRevCgst, dnRevSgst, debitNoteCount: debitNotes.length,
    };
  })();

  // HSN summary — server-computed (GET /reports/hsn-summary), already
  // signs credit notes correctly and excludes quotations/lorry receipts
  // (see TRACKER.md's finding on the sign bug this used to have). Remapped
  // to the field names this page's own JSX/CSV/print already use, so the
  // rendering below needed no changes beyond this mapping. `unit` comes
  // through as the server's fixed UQC placeholder ('OTH') rather than a
  // real per-product unit string — a deliberate, expected difference now
  // that this is the single real source of truth instead of a second,
  // independently-computed client copy.
  const hsnRows = [...hsnSummaryData]
    .map(r => ({
      hsnSac: r.hsnSac, taxable: r.totalTaxable, cgst: r.cgst, sgst: r.sgst, igst: r.igst,
      total: r.totalTaxable + r.cgst + r.sgst + r.igst, qty: r.totalQty, unit: r.uqc,
    }))
    .sort((a, b) => b.taxable - a.taxable);

  // P&L — credit notes are sales returns (negative revenue)
  const DIRECT_EXP_CATS = new Set(['freight', 'freight_inward', 'freight_outward', 'carriage_inward', 'direct_labor', 'raw_material', 'packaging', 'manufacturing', 'production']);
  // Use INR-converted base amounts when a foreign currency invoice has an exchange rate
  const plSalesRevenue = salesInvoices.filter(i => i.docType === 'tax_invoice' || i.docType === 'bill_of_supply').reduce((s, i) => s + (i.baseCurrencyTotal != null ? i.baseCurrencyTotal - (i.taxTotal * (i.exchangeRate ?? 1)) : i.subtotal), 0);
  const plCreditNotes = salesInvoices.filter(i => i.docType === 'credit_note').reduce((s, i) => s + (i.baseCurrencyTotal != null ? i.baseCurrencyTotal - (i.taxTotal * (i.exchangeRate ?? 1)) : i.subtotal), 0);
  const plRevenue = plSalesRevenue - plCreditNotes;
  const plGst = salesInvoices.filter(i => i.docType === 'tax_invoice').reduce((s, i) => s + i.taxTotal, 0) - salesInvoices.filter(i => i.docType === 'credit_note').reduce((s, i) => s + i.taxTotal, 0);
  // Debit notes are purchase returns — they reduce net purchases, not add to them
  const plPurchases = fyPurchases.filter(p => p.docType !== 'debit_note').reduce((s, p) => s + p.subtotal, 0)
                    - fyPurchases.filter(p => p.docType === 'debit_note').reduce((s, p) => s + p.subtotal, 0);
  // Real COGS = Opening Stock + Purchases − Closing Stock — previously this
  // was just plPurchases on its own, which expensed every rupee bought this
  // FY even if it's still sitting on the shelf unsold at year-end,
  // overstating COGS (understating Gross Profit) for any business carrying
  // real inventory. openingStockValue/closingStockValue both come from the
  // same shared FIFO valuation Balance Sheet's Stock Value uses (see
  // CLAUDE.md's stock-valuation note) — real cost, not selling price.
  const plCogs = openingStockValue + plPurchases - closingStockValue;
  const plDirectExp = fyExpenses.filter(e => DIRECT_EXP_CATS.has(e.category)).reduce((s, e) => s + e.amount, 0);
  const plIndirectExp = fyExpenses.filter(e => !DIRECT_EXP_CATS.has(e.category)).reduce((s, e) => s + e.amount, 0);
  const plExpByCategory = fyExpenses.reduce((m, e) => { m[e.category] = (m[e.category] ?? 0) + e.amount; return m; }, {} as Record<string, number>);
  const plTotalExp = fyExpenses.reduce((s, e) => s + e.amount, 0);
  const plGrossProfit = plRevenue - plCogs - plDirectExp;
  // Real interest expense — a loan repayment's interest portion
  // (LoanTransaction.interestAmount), a genuine deductible "Finance Cost"
  // (Schedule III's own term), itemized as its own line rather than folded
  // into Indirect Expenses since it's a fundamentally different kind of
  // cost (cost of borrowing, not an operating expense category).
  const plFinanceCost = interestData?.interestExpense ?? 0;
  // Real interest income, not a taxable supply so deliberately not folded
  // into Revenue above — added after Gross Profit, same as Direct/Indirect
  // Expenses are itemized rather than netted silently into one opaque total.
  const plOtherIncome = interestData?.interestIncome ?? 0;
  const plNetProfit = plGrossProfit - plIndirectExp - plFinanceCost + plOtherIncome;

  // Cash ledger — server-computed (GET /reports/cash-ledger), which now
  // also includes cash-mode Purchase Payments as a third debit source
  // alongside receipts and expenses — previously invisible here entirely,
  // since "purchase payment" wasn't a real record at all (see TRACKER.md's
  // finding). Remapped to this page's own `in`/`out` field names.
  const cashEntries = (cashLedgerData?.entries ?? []).map(e => ({ date: e.date, desc: e.description, in: e.credit, out: e.debit }));

  // Ageing – Debtor (credit notes reduce the party's outstanding balance)
  const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
  // Build per-party credit note totals (keyed by partyId || partyName)
  const creditNotesByParty = invoices
    .filter(i => i.docType === 'credit_note')
    .reduce<Record<string, number>>((acc, cn) => {
      const key = cn.partyId ?? cn.partyName;
      acc[key] = (acc[key] ?? 0) + cn.total;
      return acc;
    }, {});
  const debtorRows = invoices
    .filter(i => (i.status === 'unpaid' || i.status === 'overdue') && (i.docType === 'tax_invoice' || i.docType === 'bill_of_supply'))
    .map(i => {
      const partyKey = i.partyId ?? i.partyName;
      const cnOffset = creditNotesByParty[partyKey] ?? 0;
      // Subtract credit notes from the oldest unpaid invoice first (FIFO-style offset)
      const grossOutstanding = i.total - (i.paidAmount ?? 0);
      const outstanding = Math.max(0, grossOutstanding - cnOffset);
      // Consume the credit note offset against this invoice so it's not double-applied
      if (cnOffset > 0) creditNotesByParty[partyKey] = Math.max(0, cnOffset - grossOutstanding);
      const days = i.dueDate ? Math.floor((todayDate.getTime() - new Date(i.dueDate).getTime()) / 86400000) : 0;
      const bucket = !i.dueDate || days <= 0 ? 'Current' : days <= 30 ? '1–30 d' : days <= 60 ? '31–60 d' : days <= 90 ? '61–90 d' : '90+ d';
      const color = !i.dueDate || days <= 0 ? 'var(--success)' : days <= 30 ? 'var(--warning)' : days <= 60 ? '#ea580c' : 'var(--danger)';
      return { ...i, outstanding, days: Math.max(0, days), bucket, color };
    })
    .filter(i => i.outstanding > 0)  // hide fully-covered invoices
    .sort((a, b) => b.days - a.days);

  // Ageing – Creditor (Purchases + Expenses on credit)
  type CreditorRow = { id: string; source: 'purchase' | 'expense'; ref: string; date: string; dueDate?: string; partyName: string; total: number; paymentStatus: string; days: number; bucket: string; color: string };
  function makeCreditorBucket(dueDate: string | undefined): { days: number; bucket: string; color: string } {
    const days = dueDate ? Math.floor((todayDate.getTime() - new Date(dueDate).getTime()) / 86400000) : 0;
    const bucket = !dueDate || days <= 0 ? 'Current' : days <= 30 ? '1–30 d' : days <= 60 ? '31–60 d' : days <= 90 ? '61–90 d' : '90+ d';
    const color = !dueDate || days <= 0 ? 'var(--success)' : days <= 30 ? 'var(--warning)' : days <= 60 ? '#ea580c' : 'var(--danger)';
    return { days: Math.max(0, days), bucket, color };
  }
  const creditorRows: CreditorRow[] = [
    ...purchases
      .filter(p => p.paymentStatus === 'unpaid' || p.paymentStatus === 'partial')
      .map(p => ({ id: p.id, source: 'purchase' as const, ref: p.invoiceNumber ?? '—', date: p.date, dueDate: p.dueDate, partyName: p.partyName, total: p.total, paymentStatus: p.paymentStatus ?? 'unpaid', ...makeCreditorBucket(p.dueDate) })),
    ...expenses
      .filter(e => e.paymentMode !== 'cash' && e.status !== 'paid')
      .map(e => ({ id: e.id, source: 'expense' as const, ref: e.description, date: e.date, dueDate: e.dueDate, partyName: e.vendor ?? e.category, total: e.amount, paymentStatus: e.status ?? 'unpaid', ...makeCreditorBucket(e.dueDate) })),
  ].sort((a, b) => b.days - a.days);

  // TDS / TCS
  const tdsRows = salesInvoices.filter(i => (i.tdsRate ?? 0) > 0 || (i.tcsRate ?? 0) > 0);

  // ── Stock Movement with FIFO Valuation ──────────────────────────────────────
  // FIFO: oldest stock (opening, then earliest purchases) is sold first.
  // Closing value = sum of remaining layer quantities × their individual cost rates.
  type FifoLayer = { qty: number; rate: number };
  type StockRow = {
    id: string; name: string; unit: string; hsnSac: string;
    category?: 'raw_material' | 'finished_good';
    opening: number;       // opening stock qty
    purchased: number;     // qty received from suppliers this FY
    sold: number;          // qty sold to customers this FY
    custReturned: number;  // qty returned BY customers (credit notes — stock inward)
    suppReturned: number;  // qty returned TO supplier (debit notes — stock outward)
    adjusted: number;      // net manual adjustments this FY (positive = added, negative = removed)
    consumedMfg: number;   // qty consumed as a raw material by manufacturing orders this FY
    producedMfg: number;   // qty produced as a finished good by manufacturing orders this FY
    // = opening + purchased + custReturned + adjusted + producedMfg − sold − suppReturned − consumedMfg
    closing: number;
    closingValue: number;  // FIFO-computed closing stock value
    avgRate: number;       // closingValue / closing (avg cost of remaining stock)
  };

  const stockMap: Record<string, StockRow> = {};
  const fifoLayers: Record<string, FifoLayer[]> = {}; // oldest layer first
  const productByName: Record<string, string> = {};

  // Initialise from product master — opening stock is the first FIFO layer
  for (const prod of products) {
    const openQty  = prod.openingStockQty  ?? 0;
    const openRate = prod.openingStockRate ?? prod.rate ?? 0;
    stockMap[prod.id] = {
      id: prod.id, name: prod.name, unit: prod.unit ?? 'pcs', hsnSac: prod.hsnSac ?? '', category: prod.category,
      opening: openQty, purchased: 0, sold: 0, custReturned: 0, suppReturned: 0, adjusted: 0,
      consumedMfg: 0, producedMfg: 0,
      closing: 0, closingValue: 0, avgRate: openRate,
    };
    fifoLayers[prod.id] = openQty > 0 ? [{ qty: openQty, rate: openRate }] : [];
    productByName[prod.name.toLowerCase()] = prod.id;
  }

  function resolveProductId(item: { productId?: string; description: string }): string | undefined {
    if (item.productId && stockMap[item.productId]) return item.productId;
    return productByName[item.description.toLowerCase()];
  }
  function ensureRow(pid: string, item: { description: string; hsnSac?: string; rate: number }) {
    if (!stockMap[pid]) {
      stockMap[pid] = { id: pid, name: item.description, unit: 'pcs', hsnSac: item.hsnSac ?? '', opening: 0, purchased: 0, sold: 0, custReturned: 0, suppReturned: 0, adjusted: 0, consumedMfg: 0, producedMfg: 0, closing: 0, closingValue: 0, avgRate: item.rate };
      fifoLayers[pid] = [];
    }
  }

  // 1. Add FY purchases to FIFO layers (chronological — oldest purchase first)
  const sortedPurchases = fyPurchases
    .filter(p => p.docType !== 'debit_note')
    .sort((a, b) => a.date.localeCompare(b.date));
  for (const pi of sortedPurchases) {
    for (const item of pi.items ?? []) {
      const pid = resolveProductId(item);
      if (!pid) continue;
      ensureRow(pid, item);
      stockMap[pid].purchased += item.qty;
      fifoLayers[pid].push({ qty: item.qty, rate: item.rate });
    }
  }

  // 2. Supplier returns (debit notes) — deduct from MOST RECENT layers first (LIFO for returns)
  for (const pi of fyPurchases.filter(p => p.docType === 'debit_note')) {
    for (const item of pi.items ?? []) {
      const pid = resolveProductId(item);
      if (!pid || !stockMap[pid]) continue;
      stockMap[pid].suppReturned += item.qty;
      let toDeduct = item.qty;
      for (let i = (fifoLayers[pid]?.length ?? 0) - 1; i >= 0 && toDeduct > 0; i--) {
        const take = Math.min(fifoLayers[pid][i].qty, toDeduct);
        fifoLayers[pid][i].qty -= take;
        toDeduct -= take;
      }
      fifoLayers[pid] = (fifoLayers[pid] ?? []).filter(l => l.qty > 0);
    }
  }

  // 3. Sales invoices — sold reduces FIFO from the front; credit notes add a layer back in
  for (const inv of fyInvoices) {
    for (const item of inv.items) {
      const pid = resolveProductId(item);
      if (!pid) continue;
      ensureRow(pid, item);
      if (inv.docType === 'credit_note') {
        stockMap[pid].custReturned += item.qty;
        // Customer return: add back at the most recent purchase rate (conservative)
        const recentRate = fifoLayers[pid]?.length
          ? fifoLayers[pid][fifoLayers[pid].length - 1].rate
          : stockMap[pid].avgRate;
        (fifoLayers[pid] ??= []).push({ qty: item.qty, rate: recentRate });
      } else if (inv.docType !== 'lorry_receipt' && inv.docType !== 'quotation') {
        stockMap[pid].sold += item.qty;
      }
    }
  }

  // 4. Apply stock adjustments (FY-filtered)
  for (const adj of stockAdjustments) {
    const adjDate = adj.date?.slice(0, 10) ?? '';
    if (adjDate < fyFrom || adjDate > fyTo) continue;
    const pid = adj.productId;
    if (!stockMap[pid]) continue;
    stockMap[pid].adjusted += adj.qtyChange;
    if (adj.qtyChange > 0) {
      fifoLayers[pid].push({ qty: adj.qtyChange, rate: stockMap[pid].avgRate });
    } else {
      let toDeduct = -adj.qtyChange;
      for (const layer of fifoLayers[pid]) {
        if (toDeduct <= 0) break;
        const take = Math.min(layer.qty, toDeduct);
        layer.qty -= take;
        toDeduct -= take;
      }
      fifoLayers[pid] = fifoLayers[pid].filter(l => l.qty > 0);
    }
  }

  // 5. Manufacturing — raw materials consumed (order.startDate in FY, order actually started).
  //    A 'cancelled' order had its consumption reversed by cancelOrder() server-side, so it must
  //    net to zero here too — only 'in_progress'/'completed' still reflect real Product.stock.
  for (const order of manufacturingOrders) {
    if (order.status !== 'in_progress' && order.status !== 'completed') continue;
    // startOrder() now always stamps startDate, but orders started before that fix still have it
    // null — fall back to createdAt (always set) rather than silently excluding them from the FY.
    const startDate = (order.startDate ?? order.createdAt)?.slice(0, 10) ?? '';
    if (startDate < fyFrom || startDate > fyTo) continue;
    for (const comp of order.bom?.components ?? []) {
      const pid = comp.componentProductId;
      if (!pid || !stockMap[pid]) continue;
      // Prisma Decimal fields (qty, plannedQty, wastagePercent, actualQty, actualCost below)
      // serialize to JSON as strings, not numbers — Number(...) here is load-bearing, not
      // decorative: `+=` on a string silently concatenates instead of adding.
      const effectiveQty = Number(comp.qty) * Number(order.plannedQty) * (1 + Number(comp.wastagePercent ?? 0) / 100);
      stockMap[pid].consumedMfg += effectiveQty;
    }
  }

  // 6. Manufacturing — finished goods produced (batch.completedAt in FY). Values the new layer
  //    at actual per-unit cost when known, else falls back to the product's rate.
  for (const order of manufacturingOrders) {
    const pid = order.bom?.productId;
    if (!pid || !stockMap[pid]) continue;
    for (const batch of order.batches ?? []) {
      const completedDate = batch.completedAt?.slice(0, 10) ?? '';
      if (!completedDate || completedDate < fyFrom || completedDate > fyTo) continue;
      const qty = Number(batch.actualQty ?? 0);
      if (qty <= 0) continue;
      stockMap[pid].producedMfg += qty;
      const rate = batch.actualCost != null ? Number(batch.actualCost) / qty : (stockMap[pid].avgRate || 0);
      (fifoLayers[pid] ??= []).push({ qty, rate });
    }
  }

  // 7. Compute closing qty (still computed here, client-side, from this FY's
  // own movements — unaffected by this change) and FIFO closing value/avg
  // cost — now sourced from the shared server-side valuation
  // (closingValueByProduct, ProductsService.computeStockValuation()) rather
  // than recomputed a second, independently-liable-to-drift way here. This
  // is what guarantees this report's own total now matches Balance Sheet's
  // Stock Value and the P&L's Closing Stock line exactly, for the same
  // date — see CLAUDE.md's stock-valuation note. Falls back to the old
  // client-side FIFO replay only while the server request is still in
  // flight (or for a product the server response doesn't cover yet), so
  // the report never shows a blank/zero value during that brief window.
  for (const [pid, row] of Object.entries(stockMap)) {
    row.closing = row.opening + row.purchased + row.custReturned + row.adjusted + row.producedMfg
      - row.sold - row.suppReturned - row.consumedMfg;

    const serverValuation = closingValueByProduct.get(pid);
    if (serverValuation) {
      row.closingValue = serverValuation.value;
      row.avgRate = serverValuation.avgRate;
    } else {
      // FIFO: deduct sold + manufacturing-consumed qty from oldest layers first
      const layers = (fifoLayers[pid] ?? []).map(l => ({ ...l }));
      let toDeduct = Math.max(0, row.sold + row.consumedMfg);
      for (const layer of layers) {
        if (toDeduct <= 0) break;
        const take = Math.min(layer.qty, toDeduct);
        layer.qty -= take;
        toDeduct -= take;
      }
      const remaining = layers.filter(l => l.qty > 0);
      row.closingValue = remaining.reduce((s, l) => s + l.qty * l.rate, 0);
      row.avgRate = row.closing > 0 ? row.closingValue / row.closing : 0;
    }
  }

  // Only show products with any activity this FY or a non-zero opening
  const stockRows = Object.values(stockMap)
    .filter(r => r.opening !== 0 || r.purchased !== 0 || r.sold !== 0 || r.custReturned !== 0 || r.suppReturned !== 0 || r.adjusted !== 0 || r.consumedMfg !== 0 || r.producedMfg !== 0)
    .sort((a, b) => a.name.localeCompare(b.name));
  const filteredStockRows = stockCategoryFilter ? stockRows.filter(r => r.category === stockCategoryFilter) : stockRows;

  // Reconciliation check — this report's own bottom-up computed closing qty
  // (opening + movements) vs. the live Product.stock counter the Products
  // page actually shows. These two numbers are computed from completely
  // different sources and nothing normally guarantees they agree; a real,
  // common cause is a product whose openingStockQty was never filled in
  // (see ProductsPage.tsx's "Backfill Opening Stock" and CLAUDE.md's
  // Products CSV note), but this check is general-purpose — it catches any
  // future cause of the two drifting apart, not just that one. A small
  // epsilon, not strict equality, since both are decimal quantities.
  const STOCK_EPS = 0.001;
  const productStockById = new Map(products.map(p => [p.id, Number(p.stock ?? 0)]));
  function isStockMismatched(r: StockRow): boolean {
    const live = productStockById.get(r.id);
    return live !== undefined && Math.abs(r.closing - live) > STOCK_EPS;
  }
  const stockMismatchCount = filteredStockRows.filter(isStockMismatched).length;

  // Quotation tracker
  const quotations = fyInvoices.filter(i => i.docType === 'quotation');

  // ── Print helpers (kept for export buttons) ──────────────────────────────

  function printGstr1() {
    const b2bHtml = Object.entries(gstr1b2b.reduce((m, i) => { (m[i.partyGstin!] = m[i.partyGstin!] ?? []).push(i); return m; }, {} as Record<string, typeof gstr1b2b>))
      .map(([gstin, invs]) => {
        const rows = invs.map(i => { const { cgst, igst } = gstBreakdown(i.taxType, i.taxTotal); return `<tr><td>${i.invoiceNumber ?? ''}</td><td>${i.date}</td><td class="r">${fmt(i.subtotal)}</td><td class="r">${cgst > 0 ? fmt(cgst) : '—'}</td><td class="r">${cgst > 0 ? fmt(cgst) : '—'}</td><td class="r">${igst > 0 ? fmt(igst) : '—'}</td><td class="r b">${fmt(i.total)}</td></tr>`; }).join('');
        return `<tr style="background:#f1f5f9"><td colspan="6" class="b">${invs[0]?.partyName} — ${gstin}</td><td class="r b">${fmt(invs.reduce((s, i) => s + i.total, 0))}</td></tr>${rows}`;
      }).join('');
    const b2cHtml = gstr1b2c.map(i => { const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal); return `<tr><td>${i.invoiceNumber ?? ''}</td><td>${i.date}</td><td>${i.partyName}</td><td class="r">${fmt(i.subtotal)}</td><td class="r">${cgst > 0 ? fmt(cgst) : '—'}</td><td class="r">${sgst > 0 ? fmt(sgst) : '—'}</td><td class="r">${igst > 0 ? fmt(igst) : '—'}</td><td class="r b">${fmt(i.total)}</td></tr>`; }).join('');
    const html = `<h1>GSTR-1 — ${profile?.name ?? ''}</h1><div class="sub">${from} → ${to}</div><h2 style="font-size:12px;margin:12px 0 4px">B2B (${gstr1b2b.length} invoices)</h2><table><thead><tr><th>Invoice #</th><th>Date</th><th class="r">Taxable</th><th class="r">CGST</th><th class="r">SGST</th><th class="r">IGST</th><th class="r">Total</th></tr></thead><tbody>${b2bHtml || '<tr><td colspan="7" style="text-align:center;color:#94a3b8">No B2B invoices</td></tr>'}</tbody></table><h2 style="font-size:12px;margin:12px 0 4px">B2C (Table 7) — ${gstr1b2c.length} invoices</h2><table><thead><tr><th>Invoice #</th><th>Date</th><th>Party</th><th class="r">Taxable</th><th class="r">CGST</th><th class="r">SGST</th><th class="r">IGST</th><th class="r">Total</th></tr></thead><tbody>${b2cHtml || '<tr><td colspan="8" style="text-align:center;color:#94a3b8">No B2C invoices</td></tr>'}</tbody><tfoot><tr><td colspan="3" class="b">Total</td><td class="r b">${fmt(gstr1b2c.reduce((s,i)=>s+i.subtotal,0))}</td><td class="r b">${fmt(gstr1b2c.reduce((s,i)=>s+gstBreakdown(i.taxType,i.taxTotal).cgst,0))}</td><td class="r b">${fmt(gstr1b2c.reduce((s,i)=>s+gstBreakdown(i.taxType,i.taxTotal).sgst,0))}</td><td class="r b">${fmt(gstr1b2c.reduce((s,i)=>s+gstBreakdown(i.taxType,i.taxTotal).igst,0))}</td><td class="r b">${fmt(gstr1b2c.reduce((s,i)=>s+i.total,0))}</td></tr></tfoot></table>`;
    printHtml(html, `GSTR-1 ${from} → ${to}`);
  }

  function printSalesRegister() {
    const rows = salesInvoices.filter(i => i.docType !== 'lorry_receipt');
    const signed = (i: typeof rows[0], v: number) => (i.docType === 'credit_note' ? -1 : 1) * v;
    const taxOf = (i: typeof rows[0]) => gstBreakdown(i.taxType, i.isRcm ? 0 : i.taxTotal);
    const netSub  = rows.reduce((s, i) => s + signed(i, i.subtotal), 0);
    const netCgst = rows.reduce((s, i) => s + signed(i, taxOf(i).cgst), 0);
    const netSgst = rows.reduce((s, i) => s + signed(i, taxOf(i).sgst), 0);
    const netIgst = rows.reduce((s, i) => s + signed(i, taxOf(i).igst), 0);
    const netTot  = rows.reduce((s, i) => s + signed(i, i.total), 0);
    const html = `<h1>Sales Register — ${profile?.name ?? ''}</h1><div class="sub">${from} → ${to}</div><table><thead><tr><th>Invoice #</th><th>Date</th><th>Party</th><th>GSTIN</th><th class="r">Subtotal</th><th class="r">CGST</th><th class="r">SGST</th><th class="r">IGST</th><th class="r">Total</th><th>Status</th></tr></thead><tbody>${rows.map(i => { const s = i.docType === 'credit_note' ? -1 : 1; const { cgst, sgst, igst } = taxOf(i); const style = i.docType === 'credit_note' ? ' style="color:#dc2626"' : i.isRcm ? ' style="color:#9f1239"' : ''; return `<tr${style}><td>${i.invoiceNumber ?? ''}</td><td>${i.date}</td><td>${i.partyName}</td><td style="font-size:9px">${i.partyGstin ?? '—'}</td><td class="r">${fmt(s * i.subtotal)}</td><td class="r">${cgst > 0 ? fmt(s * cgst) : '—'}</td><td class="r">${sgst > 0 ? fmt(s * sgst) : '—'}</td><td class="r">${igst > 0 ? fmt(s * igst) : '—'}</td><td class="r b">${fmt(s * i.total)}</td><td>${i.isRcm ? 'RCM' : i.status}</td></tr>`; }).join('')}</tbody><tfoot><tr><td colspan="4" class="b">Total (${rows.length})</td><td class="r b">${fmt(netSub)}</td><td class="r b">${fmt(netCgst)}</td><td class="r b">${fmt(netSgst)}</td><td class="r b">${fmt(netIgst)}</td><td class="r b">${fmt(netTot)}</td><td></td></tr></tfoot></table>`;
    printHtml(html, `Sales Register ${from} to ${to}`);
  }

  function printPurchaseRegister() {
    const sign = (p: typeof fyPurchases[0]) => p.docType === 'debit_note' ? -1 : 1;
    const netSub = fyPurchases.reduce((s, p) => s + sign(p) * p.subtotal, 0);
    const netTot = fyPurchases.reduce((s, p) => s + sign(p) * p.total, 0);
    const html = `<h1>Purchase Register — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>PI #</th><th>Type</th><th>Supplier Inv</th><th>Date</th><th>Supplier</th><th class="r">Subtotal</th><th class="r">Tax</th><th class="r">Total</th><th>Payment</th></tr></thead><tbody>${fyPurchases.map(p => { const dn = p.docType === 'debit_note'; return `<tr style="${dn ? 'color:#d97706' : ''}"><td>${p.invoiceNumber ?? ''}</td><td>${dn ? 'Debit Note' : 'Purchase'}</td><td>${p.supplierInvoice ?? '—'}</td><td>${p.date}</td><td>${p.partyName}</td><td class="r ${dn ? 'orange' : ''}">${dn ? '−' : ''}${fmt(p.subtotal)}</td><td class="r">${dn ? '−' : ''}${fmt(p.taxTotal)}</td><td class="r b ${dn ? 'orange' : ''}">${dn ? '−' : ''}${fmt(p.total)}</td><td>${p.paymentStatus}</td></tr>`; }).join('')}</tbody><tfoot><tr><td colspan="5" class="b">Net Total (${fyPurchases.length})</td><td class="r b">${fmt(netSub)}</td><td></td><td class="r b">${fmt(netTot)}</td><td></td></tr></tfoot></table>`;
    printHtml(html, `Purchase Register ${fy}`);
  }

  function printPartyLedger() {
    if (!partyId || !ledgerData.length) return;
    const party = parties.find(p => p.id === partyId);
    const html = `<h1>Party Ledger: ${party?.name ?? ''}</h1><div class="sub">${profile?.name ?? ''} | As of ${today()}</div><table><thead><tr><th>Date</th><th>Type</th><th>Description</th><th class="r">Debit</th><th class="r">Credit</th><th class="r">Balance</th></tr></thead><tbody>${ledgerData.map(e => `<tr><td>${e.date}</td><td>${e.type}</td><td>${e.description}${e.invoiceNumber ? ` #${e.invoiceNumber}` : ''}</td><td class="r red">${e.debit > 0 ? fmt(e.debit) : '—'}</td><td class="r green">${e.credit > 0 ? fmt(e.credit) : '—'}</td><td class="r b">${fmt(Math.abs(e.balance))} ${e.balance > 0 ? 'Dr' : e.balance < 0 ? 'Cr' : ''}</td></tr>`).join('')}</tbody></table>`;
    printHtml(html, `Ledger - ${party?.name ?? ''}`);
  }

  function handleGstr2aUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    setGstr2aError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = toGstr2aRows(JSON.parse(reader.result as string));
        if (!parsed.length) { setGstr2aError('No B2B entries found.'); return; }
        const recon = parsed.map(r => {
          const match = purchases.find(p => (p.partyGstin ?? '').toUpperCase() === r.supplierGstin.toUpperCase() && (p.invoiceNumber.toUpperCase() === r.invoiceNumber.toUpperCase() || p.invoiceNumber.replace(/[-\/]/g, '') === r.invoiceNumber.replace(/[-\/]/g, '')));
          return match ? { ...r, matchStatus: 'matched' as const, localInvoiceNumber: match.invoiceNumber, taxDiff: r.totalTax - Number(match.taxTotal) } : r;
        });
        for (const p of purchases) {
          if (!recon.some(r => r.matchStatus === 'matched' && r.localInvoiceNumber === p.invoiceNumber) && p.partyGstin) {
            recon.push({ supplierGstin: p.partyGstin, supplierName: p.partyName, invoiceNumber: p.invoiceNumber, invoiceDate: p.date, taxableValue: Number(p.subtotal), igst: 0, cgst: 0, sgst: 0, totalTax: Number(p.taxTotal), matchStatus: 'in_local_only' });
          }
        }
        setGstr2aRows(recon);
      } catch { setGstr2aError('Invalid JSON file.'); }
      finally { if (gstr2aRef.current) gstr2aRef.current.value = ''; }
    };
    reader.readAsText(file);
  }

  // ── Badge helper ─────────────────────────────────────────────────────────
  const AgeBadge = ({ bucket, color }: { bucket: string; color: string }) => (
    <span style={{ fontSize: '0.7rem', fontWeight: 600, color, background: color + '18', padding: '0.12rem 0.45rem', borderRadius: 999, whiteSpace: 'nowrap' }}>{bucket}</span>
  );

  const StatusBadge = ({ status }: { status: string }) => (
    <span className={`badge badge-${status}`} style={{ fontSize: '0.72rem' }}>{status}</span>
  );

  // ── Period selector component ─────────────────────────────────────────────
  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">Reports</h1></div>
      {error && <div className="error-box">{error}</div>}

      {/* Category tabs — which reports to show */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.25rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.1rem' }}>
        {REPORT_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => { setActiveTab(cat.id); setActiveReport(null); }}
            style={{
              appearance: 'none', border: 'none', background: 'transparent', cursor: 'pointer',
              font: 'inherit', fontSize: '0.85rem', fontWeight: 600,
              color: activeTab === cat.id ? 'var(--primary)' : 'var(--text-muted)',
              padding: '0.65rem 0.9rem', borderRadius: '7px 7px 0 0',
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              borderBottom: activeTab === cat.id ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {cat.label}
            <span style={{
              fontSize: '0.68rem', fontWeight: 700,
              color: activeTab === cat.id ? 'var(--primary)' : 'var(--text-muted)',
              background: activeTab === cat.id ? 'var(--primary-light)' : 'var(--surface-alt)',
              border: activeTab === cat.id ? 'none' : '1px solid var(--border)',
              borderRadius: 999, padding: '0.02rem 0.4rem',
            }}>{cat.reports.length}</span>
          </button>
        ))}
      </div>

      {/* Report menu (tiles) for the active category, or a back link once one is open */}
      {(() => {
        const activeCat = REPORT_CATEGORIES.find(c => c.id === activeTab)!;
        if (!activeReport) {
          return (
            <div style={{ marginBottom: '1.25rem' }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 1rem', maxWidth: '62ch' }}>{activeCat.blurb}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.9rem' }}>
                {activeCat.reports.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setActiveReport(r.id)}
                    className="card"
                    style={{ textAlign: 'left', border: 'none', cursor: 'pointer', padding: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
                  >
                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{r.title}</span>
                    <span style={{ fontSize: '0.79rem', color: 'var(--text-muted)', flex: 1 }}>{r.desc}</span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      View report →
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        }
        return (
          <button
            type="button"
            onClick={() => setActiveReport(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontWeight: 700, fontSize: '0.85rem', padding: 0, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
          >
            ← Back to {activeCat.label} reports
          </button>
        );
      })()}

      {/* Period selector */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '0.9rem 1.25rem' }}>
        <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-2)' }}>
            Financial Year
            <select value={fy} onChange={e => { setFy(e.target.value); setDateMode('fy'); }} style={{ width: 'auto' }}>
              {FY_LIST.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          {/* Divider */}
          <span style={{ color: 'var(--border)', fontSize: '1.2rem', userSelect: 'none' }}>|</span>

          {/* Custom date range toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => { setDateMode(m => m === 'custom' ? 'fy' : 'custom'); }}
              style={{
                padding: '0.3rem 0.75rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                border: `2px solid ${dateMode === 'custom' ? 'var(--primary)' : '#e2e8f0'}`,
                background: dateMode === 'custom' ? '#eff6ff' : 'white',
                color: dateMode === 'custom' ? 'var(--primary)' : '#64748b',
              }}
            >
              Custom Date
            </button>
            {dateMode === 'custom' && (
              <>
                <input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  style={{ width: 145, fontSize: '0.83rem' }}
                />
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>→</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  style={{ width: 145, fontSize: '0.83rem' }}
                />
                <button type="button" onClick={() => { setCustomFrom(''); setCustomTo(''); setDateMode('fy'); }}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', padding: '0 0.2rem' }} title="Clear custom date">✕</button>
              </>
            )}
          </div>

          <span style={{ fontSize: '0.78rem', color: dateMode === 'custom' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: dateMode === 'custom' ? 600 : 400 }}>
            {dateMode === 'custom'
              ? `Custom range: ${customFrom || '…'} → ${customTo || '…'}`
              : `Showing FY ${fy}: ${from} → ${to}`}
          </span>
        </div>
      </div>

      {/* GSTR-1 Filing Modal */}
      {gstr1FilingModal && gstr1ValidationResult && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <span className="modal-title">GSTR-1 Pre-Filing Validation</span>
              <button onClick={() => { setGstr1FilingModal(false); setGstr1FilingMsg(null); setGstr1FilingOtp(''); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {gstr1ValidationResult.checks.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  <span style={{ color: c.passed ? 'var(--success)' : 'var(--danger)', fontWeight: 700, flexShrink: 0 }}>{c.passed ? '✓' : '✗'}</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{c.detail}</div>
                  </div>
                </div>
              ))}
              {gstr1ValidationResult.allPassed && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>All checks passed. Enter your GST portal OTP to file GSTR-1 for {from} → {to}.</p>
                  <input type="text" placeholder="Enter OTP from GST portal" value={gstr1FilingOtp} onChange={e => setGstr1FilingOtp(e.target.value)}
                    style={{ width: '100%', marginBottom: '0.75rem', fontSize: '0.875rem' }} />
                  {gstr1FilingMsg && <div style={{ color: 'var(--success)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{gstr1FilingMsg}</div>}
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost" onClick={() => setGstr1FilingModal(false)}>Cancel</button>
                    <button className="btn btn-primary" disabled={!gstr1FilingOtp || gstr1FilingLoading} onClick={async () => {
                      setGstr1FilingLoading(true);
                      try {
                        const res = await gstrFilingApi.fileGstr1(from, to, gstr1FilingOtp);
                        setGstr1FilingMsg(`Filed successfully! Ack: ${res.ackNumber}. ${res.note ?? ''}`);
                        setGstr1FilingOtp('');
                      } catch (e) { setGstr1FilingMsg(e instanceof Error ? e.message : 'Filing failed'); }
                      finally { setGstr1FilingLoading(false); }
                    }}>File GSTR-1</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* GSTR-1                                                             */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'gst' && activeReport === 'gstr1' && <ReportCard
        title={`GSTR-1 — Outward Supplies  (${`${from} → ${to}`} ${fy})`}
        actions={<>
          <Btn k="gstr1print" label="🖨 Print" onClick={printGstr1} />
          <Btn k="gstr1csv" label="⬇ CSV" onClick={async () => {
            if (!guardExport()) return;
            const token = sessionStorage.getItem('cl_token');
            const res = await fetch(`${API_BASE}/reports/sales-register/csv?from=${from}&to=${to}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) throw new Error('Download failed');
            const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `GSTR1_${fy}_${`${from} → ${to}`}.csv`; a.click(); URL.revokeObjectURL(url);
          }} />
          <Btn k="gstr1json" label="⬇ JSON" onClick={async () => {
            if (!guardExport()) return;
            const token = sessionStorage.getItem('cl_token');
            const res = await fetch(`${API_BASE}/reports/gstr1/json?from=${from}&to=${to}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) throw new Error('Download failed');
            const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `GSTR1_${fy}_${`${from} → ${to}`}.json`; a.click(); URL.revokeObjectURL(url);
          }} />
          {gstr1FilingMsg && <span style={{ fontSize: '0.78rem', color: 'var(--success)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{gstr1FilingMsg}</span>}
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem', borderColor: 'var(--primary)', color: 'var(--primary)' }} onClick={async () => {
            setGstr1FilingLoading(true); setGstr1ValidationResult(null); setGstr1FilingMsg(null);
            try {
              const res = await gstrFilingApi.validateGstr1(from, to);
              setGstr1ValidationResult(res);
              setGstr1FilingModal(true);
            } finally { setGstr1FilingLoading(false); }
          }} disabled={gstr1FilingLoading}>Validate & File</button>
        </>}
      >
        {/* B2B */}
        <div style={{ padding: '0.6rem 1.25rem 0.25rem', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          B2B Supplies (Table 4) — {gstr1b2b.length} invoices · {fmtN(gstr1b2b.reduce((s, i) => s + i.total, 0))}
        </div>
        <table>
          <thead>
            <tr><th>Invoice #</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Place of Supply</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>CGST</th><th style={{ textAlign: 'right' }}>SGST</th><th style={{ textAlign: 'right' }}>IGST</th><th style={{ textAlign: 'right' }}>Total</th></tr>
          </thead>
          <tbody>
            {gstr1b2b.length === 0
              ? <EmptyRow cols={10} msg="No B2B invoices in this period" />
              : gstr1b2b.map(i => {
                const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal);
                return (
                  <tr key={i.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                    <td>{i.date}</td>
                    <td>{i.partyName}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{i.partyGstin}</td>
                    <td>{i.placeOfSupply ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmtN(i.subtotal)}</td>
                    <td style={{ textAlign: 'right' }}>{cgst > 0 ? fmtN(cgst) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{sgst > 0 ? fmtN(sgst) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{igst > 0 ? fmtN(igst) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(i.total)}</td>
                  </tr>
                );
              })
            }
          </tbody>
          {gstr1b2b.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} style={{ fontWeight: 700 }}>B2B Total</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2b.reduce((s, i) => s + i.subtotal, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2b.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).cgst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2b.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).sgst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2b.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).igst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2b.reduce((s, i) => s + i.total, 0))}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {/* B2B RCM Table 4B */}
        {gstr1b2bRcm.length > 0 && (
          <>
            <div style={{ padding: '0.6rem 1.25rem 0.25rem', fontSize: '0.78rem', fontWeight: 700, color: '#9f1239', background: '#fff1f2', borderBottom: '1px solid #fecdd3', borderTop: '1px solid var(--border)' }}>
              B2B RCM Supplies (Table 4B) — {gstr1b2bRcm.length} invoices — Tax payable by recipient
            </div>
            <table>
              <thead>
                <tr><th>Invoice #</th><th>Date</th><th>Party</th><th>GSTIN</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>CGST</th><th style={{ textAlign: 'right' }}>SGST</th><th style={{ textAlign: 'right' }}>IGST</th><th style={{ textAlign: 'right' }}>Total</th></tr>
              </thead>
              <tbody>
                {gstr1b2bRcm.map(i => {
                  const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal);
                  return (
                    <tr key={i.id} style={{ background: 'var(--row-tint-red)' }}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                      <td>{i.date}</td>
                      <td>{i.partyName}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{i.partyGstin ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtN(i.subtotal)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{cgst > 0 ? fmtN(cgst) : '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{sgst > 0 ? fmtN(sgst) : '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{igst > 0 ? fmtN(igst) : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(i.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontWeight: 700 }}>Total RCM</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2bRcm.reduce((s, i) => s + i.subtotal, 0))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2bRcm.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).cgst, 0))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2bRcm.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).sgst, 0))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2bRcm.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).igst, 0))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2bRcm.reduce((s, i) => s + i.total, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
        {/* B2C */}
        <div style={{ padding: '0.6rem 1.25rem 0.25rem', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', background: 'var(--bg)', borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)' }}>
          B2C Supplies (Table 7) — {gstr1b2c.length} invoices
        </div>
        <table>
          <thead>
            <tr>
              <th>Invoice #</th><th>Date</th><th>Party</th><th>Doc Type</th>
              <th style={{ textAlign: 'right' }}>Taxable</th>
              <th style={{ textAlign: 'right' }}>CGST</th>
              <th style={{ textAlign: 'right' }}>SGST</th>
              <th style={{ textAlign: 'right' }}>IGST</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {gstr1b2c.length === 0
              ? <EmptyRow cols={9} msg="No B2C invoices in this period" />
              : gstr1b2c.map(i => {
                const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal);
                return (
                  <tr key={i.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                    <td>{i.date}</td>
                    <td>{i.partyName}</td>
                    <td><span style={{ textTransform: 'capitalize', fontSize: '0.78rem' }}>{i.docType?.replace(/_/g, ' ')}</span></td>
                    <td style={{ textAlign: 'right' }}>{fmtN(i.subtotal)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{cgst > 0 ? fmtN(cgst) : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{sgst > 0 ? fmtN(sgst) : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-2)' }}>{igst > 0 ? fmtN(igst) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(i.total)}</td>
                  </tr>
                );
              })
            }
          </tbody>
          {gstr1b2c.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={4} style={{ fontWeight: 700 }}>Total</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2c.reduce((s, i) => s + i.subtotal, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2c.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).cgst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2c.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).sgst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2c.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).igst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(gstr1b2c.reduce((s, i) => s + i.total, 0))}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {/* CDNR */}
        {gstr1cdnr.length > 0 && (
          <>
            <div style={{ padding: '0.6rem 1.25rem 0.25rem', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', background: 'var(--bg)', borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)' }}>
              CDNR — Credit / Debit Notes (Table 9B) — {gstr1cdnr.length} notes
            </div>
            <table>
              <thead><tr><th>Note #</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Type</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>Total</th></tr></thead>
              <tbody>
                {gstr1cdnr.map(i => (
                  <tr key={i.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                    <td>{i.date}</td><td>{i.partyName}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{i.partyGstin ?? 'Unregistered'}</td>
                    <td><span style={{ fontWeight: 700, color: i.docType === 'credit_note' ? 'var(--danger)' : 'var(--warning)' }}>{i.docType === 'credit_note' ? 'Credit' : 'Debit'}</span></td>
                    <td style={{ textAlign: 'right' }}>{fmtN(i.subtotal)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{fmtN(i.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* GSTR-3B                                                            */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'gst' && activeReport === 'gstr3b' && <ReportCard
        title={`GSTR-3B — Summary Return  (${`${from} → ${to}`} ${fy})`}
        actions={<Btn k="gstr3bcsv" label="⬇ CSV" onClick={async () => {
          if (!guardExport()) return;
          const token = sessionStorage.getItem('cl_token');
          const res = await fetch(`${API_BASE}/reports/gstr3b/csv?from=${from}&to=${to}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
          if (!res.ok) throw new Error('Download failed');
          const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `GSTR3B_${fy}_${`${from} → ${to}`}.csv`; a.click(); URL.revokeObjectURL(url);
        }} />}
      >
        <div className="form-row form-row-2" style={{ gap: 0, marginBottom: 0 }}>
          {/* 3.1 Output */}
          <div style={{ borderRight: '1px solid var(--border)', padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', marginBottom: '0.75rem' }}>3.1  Outward Tax Liability</div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>3.1(a) Regular Outward (Taxable)</div>
            {[
              { label: 'Taxable Value (net of CN/DN)', val: gstr3b.outTaxable },
              { label: 'IGST', val: gstr3b.outIgst },
              { label: 'CGST', val: gstr3b.outCgst },
              { label: 'SGST / UTGST', val: gstr3b.outSgst },
              { label: 'Nil / Exempt (Bill of Supply)', val: gstr3b.nilExempt },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-2)' }}>{r.label}</span>
                <span style={{ fontWeight: 600 }}>{fmtN(r.val)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0 0.6rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--danger)' }}>
              <span>Total Output Tax</span>
              <span>{fmtN(gstr3b.outTotal)}</span>
            </div>
            {/* 3.1(d) RCM Outward */}
            <div style={{ borderTop: '1.5px dashed var(--border)', paddingTop: '0.6rem', marginTop: '0.25rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9f1239', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>3.1(d) Supplies under RCM ({gstr3b.rcmCount} invoices)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-2)' }}>Taxable Value</span>
                <span style={{ fontWeight: 600 }}>{fmtN(gstr3b.rcmTaxable)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Output Tax (supplier liability)</span>
                <span style={{ fontWeight: 600, color: 'var(--success)' }}>₹0 — Recipient pays</span>
              </div>
            </div>
          </div>
          {/* 4 ITC */}
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', marginBottom: '0.75rem' }}>4.  ITC Available — FY {fy} (Purchases + Expenses)</div>
            {[
              { label: 'IGST (4A5)', val: gstr3b.itcIgst },
              { label: 'CGST (4A5)', val: gstr3b.itcCgst },
              { label: 'SGST / UTGST (4A5)', val: gstr3b.itcSgst },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-2)' }}>{r.label}</span>
                <span style={{ fontWeight: 600, color: 'var(--success)' }}>{fmtN(r.val)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontSize: '0.85rem', fontWeight: 700, color: 'var(--success)', marginTop: '0.25rem' }}>
              <span>Total ITC</span><span>{fmtN(gstr3b.itcTotal)}</span>
            </div>
            {gstr3b.debitNoteCount > 0 && (
              <div style={{ borderTop: '1.5px dashed var(--border)', paddingTop: '0.6rem', marginTop: '0.25rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9f1239', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>4B Input Tax Reversal (Debit Notes — {gstr3b.debitNoteCount})</div>
                {[
                  { label: 'IGST Reversed', val: gstr3b.dnRevIgst },
                  { label: 'CGST Reversed', val: gstr3b.dnRevCgst },
                  { label: 'SGST / UTGST Reversed', val: gstr3b.dnRevSgst },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                    <span style={{ color: 'var(--text-2)' }}>{r.label}</span>
                    <span style={{ fontWeight: 600, color: '#dc2626' }}>−{fmtN(r.val)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Net payable — Section 49 CGST Act ITC cross-utilization applied */}
        <div style={{ borderTop: '2px solid var(--border)', padding: '0.6rem 1.25rem 0.4rem', background: '#fafafa' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
            6. Net Tax Payable (after ITC set-off as per Sec 49 CGST Act)
          </div>
          <div className="form-row form-row-3" style={{ gap: 0, marginBottom: 0 }}>
            {[
              { label: 'Net IGST Payable', val: gstr3b.netIgst },
              { label: 'Net CGST Payable', val: gstr3b.netCgst },
              { label: 'Net SGST Payable', val: gstr3b.netSgst },
            ].map((r, i) => (
              <div key={r.label} style={{ padding: '0.5rem 0.75rem', borderRight: i < 2 ? '1px solid var(--border)' : undefined, textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>{r.label}</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: r.val > 0 ? 'var(--danger)' : 'var(--success)' }}>
                  {r.val > 0 ? fmtN(r.val) : <span style={{ color: 'var(--success)' }}>NIL</span>}
                </div>
              </div>
            ))}
          </div>
          {(gstr3b.carryIgst > 0 || gstr3b.carryCgst > 0 || gstr3b.carrySgst > 0) && (
            <div style={{ borderTop: '1px dashed var(--border)', marginTop: '0.5rem', paddingTop: '0.4rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0369a1', marginBottom: '0.3rem' }}>ITC Carry Forward (Unutilized)</div>
              <div className="form-row form-row-3" style={{ gap: 0, marginBottom: 0, fontSize: '0.8rem' }}>
                {[
                  { label: 'IGST ITC c/f', val: gstr3b.carryIgst },
                  { label: 'CGST ITC c/f', val: gstr3b.carryCgst },
                  { label: 'SGST ITC c/f', val: gstr3b.carrySgst },
                ].map((r, i) => r.val > 0 ? (
                  <div key={r.label} style={{ padding: '0.25rem 0.75rem', textAlign: 'center', borderRight: i < 2 ? '1px solid var(--border)' : undefined }}>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{r.label}</div>
                    <div style={{ fontWeight: 700, color: '#0369a1' }}>{fmtN(r.val)}</div>
                  </div>
                ) : <div key={r.label} />)}
              </div>
            </div>
          )}
        </div>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* RCM Supplies Report                                                */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'gst' && activeReport === 'rcm' && (() => {
        const rcmInvoices = salesInvoices.filter(i => i.isRcm && !isCdnr(i));
        function printRcmReport() {
          const rows = rcmInvoices.map(i => {
            const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal);
            return `<tr><td>${i.invoiceNumber ?? ''}</td><td>${i.date}</td><td>${i.partyName}</td><td style="font-size:9px">${i.partyGstin ?? '—'}</td><td class="r">${fmt(i.subtotal)}</td><td class="r">${cgst > 0 ? fmt(cgst) : '—'}</td><td class="r">${sgst > 0 ? fmt(sgst) : '—'}</td><td class="r">${igst > 0 ? fmt(igst) : '—'}</td><td class="r b">${fmt(i.total)}</td><td>${i.status}</td></tr>`;
          }).join('');
          const totTaxable = rcmInvoices.reduce((s, i) => s + i.subtotal, 0);
          const totTotal = rcmInvoices.reduce((s, i) => s + i.total, 0);
          const html = `<h1>RCM Supplies Report — ${profile?.name ?? ''}</h1><div class="sub">${from} → ${to} | Tax payable by recipient</div><table><thead><tr><th>Invoice #</th><th>Date</th><th>Party</th><th>GSTIN</th><th class="r">Taxable</th><th class="r">CGST</th><th class="r">SGST</th><th class="r">IGST</th><th class="r">Total</th><th>Status</th></tr></thead><tbody>${rows || '<tr><td colspan="10" style="text-align:center;color:#94a3b8">No RCM invoices</td></tr>'}</tbody><tfoot><tr><td colspan="4" class="b">Total (${rcmInvoices.length})</td><td class="r b">${fmt(totTaxable)}</td><td colspan="3"></td><td class="r b">${fmt(totTotal)}</td><td></td></tr></tfoot></table><div style="margin-top:16px;padding:10px;background:#fff1f2;border:1px solid #fecdd3;border-radius:6px;font-size:11px;color:#9f1239"><strong>Note:</strong> For RCM supplies, the output tax liability rests with the recipient. Supplier declares taxable value only — no GST is collected.</div>`;
          printHtml(html, `RCM Supplies ${from} to ${to}`);
        }
        return (
          <ReportCard
            title={`RCM Supplies Report  (${from} → ${to})  — ${rcmInvoices.length} invoices`}
            actions={<>
              <Btn k="rcmcsv" label="⬇ Excel" onClick={() => {
                if (!guardExport()) return;
                const header = ['Invoice #','Date','Party','GSTIN','Taxable','CGST','SGST','IGST','Total','Status'];
                const lines = rcmInvoices.map(i => {
                  const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal);
                  return [i.invoiceNumber ?? '', i.date, i.partyName, i.partyGstin ?? '', i.subtotal.toFixed(2), cgst.toFixed(2), sgst.toFixed(2), igst.toFixed(2), i.total.toFixed(2), i.status].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
                });
                downloadCsv([header.join(','), ...lines].join('\n'), `RCM_Supplies_${from}_${to}.csv`);
              }} />
              <Btn k="rcmpdf" label="🖨 Print" onClick={printRcmReport} />
            </>}
          >
            {rcmInvoices.length === 0
              ? <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No RCM invoices in this period</div>
              : <>
                <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 0, padding: '0.6rem 1.25rem', fontSize: '0.8rem', color: '#9f1239', borderBottom: '1px solid #fecdd3' }}>
                  RCM (Reverse Charge) — tax liability is on the <strong>recipient</strong>. Supplier declares taxable value only; output tax for supplier = ₹0.
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>Invoice #</th><th>Date</th><th>Party</th><th>GSTIN</th>
                      <th style={{ textAlign: 'right' }}>Taxable</th>
                      <th style={{ textAlign: 'right' }}>CGST</th>
                      <th style={{ textAlign: 'right' }}>SGST</th>
                      <th style={{ textAlign: 'right' }}>IGST</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rcmInvoices.map(i => {
                      const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.taxTotal);
                      return (
                        <tr key={i.id} style={{ background: 'var(--row-tint-red)' }}>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                          <td>{i.date}</td>
                          <td style={{ fontWeight: 500 }}>{i.partyName}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{i.partyGstin ?? '—'}</td>
                          <td style={{ textAlign: 'right' }}>{fmtN(i.subtotal)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{cgst > 0 ? fmtN(cgst) : '—'}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{sgst > 0 ? fmtN(sgst) : '—'}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{igst > 0 ? fmtN(igst) : '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(i.total)}</td>
                          <td><StatusBadge status={i.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ fontWeight: 700 }}>Total ({rcmInvoices.length} invoices)</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(rcmInvoices.reduce((s, i) => s + i.subtotal, 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(rcmInvoices.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).cgst, 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(rcmInvoices.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).sgst, 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(rcmInvoices.reduce((s, i) => s + gstBreakdown(i.taxType, i.taxTotal).igst, 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(rcmInvoices.reduce((s, i) => s + i.total, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </>
            }
          </ReportCard>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Sales Register                                                     */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'salesPurchases' && activeReport === 'salesRegister' && <ReportCard
        title={`Sales Register  FY ${fy}  (${salesInvoices.length} invoices)`}
        actions={<>
          <Btn k="salescsv" label="⬇ CSV" onClick={() => {
            if (!guardExport()) return;
            const header = ['Invoice #','Date','Due Date','Party','GSTIN','Doc Type','Tax Type','Subtotal','CGST','SGST','IGST','Total Tax','Total','Paid','Balance','Status'];
            const lines = salesInvoices.map(i => { const s = i.docType === 'credit_note' ? -1 : 1; const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.isRcm ? 0 : i.taxTotal); const paid = i.paidAmount ?? 0; return [i.invoiceNumber ?? '', i.date, i.dueDate ?? '', i.partyName, i.partyGstin ?? '', i.docType ?? '', i.taxType ?? '', (s * i.subtotal).toFixed(2), (s * cgst).toFixed(2), (s * sgst).toFixed(2), (s * igst).toFixed(2), (s * (i.isRcm ? 0 : i.taxTotal)).toFixed(2), (s * i.total).toFixed(2), paid.toFixed(2), (s * i.total - paid).toFixed(2), i.isRcm ? 'RCM' : i.status].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','); });
            downloadCsv([header.join(','), ...lines].join('\n'), `SalesRegister_${fy}.csv`);
          }} />
          <Btn k="salespdf" label="🖨 Print" onClick={printSalesRegister} />
        </>}
      >
        <table>
          <thead>
            <tr>
              <th>Invoice #</th><th>Date</th><th>Party</th><th>Doc Type</th><th>Tax Type</th>
              <th style={{ textAlign: 'right' }}>Subtotal</th>
              <th style={{ textAlign: 'right' }}>CGST</th>
              <th style={{ textAlign: 'right' }}>SGST</th>
              <th style={{ textAlign: 'right' }}>IGST</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Paid</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {salesInvoices.length === 0
              ? <EmptyRow cols={13} msg={`No invoices in this period`} />
              : salesInvoices.map(i => {
                const isCn = i.docType === 'credit_note';
                const sign = isCn ? -1 : 1;
                const paid = i.paidAmount ?? 0;
                const { cgst, sgst, igst } = gstBreakdown(i.taxType, i.isRcm ? 0 : i.taxTotal);
                return (
                  <tr key={i.id} style={isCn ? { background: 'var(--row-tint-rose)' } : i.isRcm ? { background: 'var(--row-tint-red)' } : undefined}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{i.date}</td>
                    <td>{i.partyName}</td>
                    <td style={{ fontSize: '0.75rem', textTransform: 'capitalize', color: isCn || i.isRcm ? 'var(--danger)' : undefined }}>{i.isRcm ? 'RCM Supply' : i.docType?.replace(/_/g, ' ')}</td>
                    <td style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>{i.taxType}</td>
                    <td style={{ textAlign: 'right', color: isCn ? 'var(--danger)' : undefined }}>{isCn ? '−' : ''}{fmtN(i.subtotal)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{cgst > 0 ? `${isCn ? '−' : ''}${fmtN(cgst)}` : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{sgst > 0 ? `${isCn ? '−' : ''}${fmtN(sgst)}` : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{igst > 0 ? `${isCn ? '−' : ''}${fmtN(igst)}` : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: isCn ? 'var(--danger)' : undefined }}>{isCn ? '−' : ''}{fmtN(i.total)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--success)' }}>{paid > 0 ? fmtN(paid) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: paid < i.total * sign ? 600 : 400, color: paid < i.total * sign ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {paid < i.total ? fmtN(i.total - paid) : '—'}
                    </td>
                    <td><StatusBadge status={i.status} /></td>
                  </tr>
                );
              })
            }
          </tbody>
          {salesInvoices.length > 0 && (() => {
            const signedTotal = (fn: (i: typeof salesInvoices[0]) => number) =>
              salesInvoices.reduce((s, i) => s + (i.docType === 'credit_note' ? -1 : 1) * fn(i), 0);
            const netSub  = signedTotal(i => i.subtotal);
            const netCgst = signedTotal(i => gstBreakdown(i.taxType, i.isRcm ? 0 : i.taxTotal).cgst);
            const netSgst = signedTotal(i => gstBreakdown(i.taxType, i.isRcm ? 0 : i.taxTotal).sgst);
            const netIgst = signedTotal(i => gstBreakdown(i.taxType, i.isRcm ? 0 : i.taxTotal).igst);
            const netTot  = signedTotal(i => i.total);
            const netPaid = salesInvoices.reduce((s, i) => s + (i.paidAmount ?? 0), 0);
            const netBal  = netTot - netPaid;
            return (
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 700 }}>Total ({salesInvoices.length})</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: netSub < 0 ? 'var(--danger)' : undefined }}>{netSub < 0 ? '−' : ''}{fmtN(Math.abs(netSub))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{netCgst < 0 ? '−' : ''}{fmtN(Math.abs(netCgst))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{netSgst < 0 ? '−' : ''}{fmtN(Math.abs(netSgst))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{netIgst < 0 ? '−' : ''}{fmtN(Math.abs(netIgst))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: netTot < 0 ? 'var(--danger)' : undefined }}>{netTot < 0 ? '−' : ''}{fmtN(Math.abs(netTot))}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{fmtN(netPaid)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: netBal > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>{netBal > 0 ? fmtN(netBal) : '—'}</td>
                  <td />
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Credit Note Register                                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'salesPurchases' && activeReport === 'creditNoteRegister' && (() => {
        const cnRows = salesInvoices.filter(i => i.docType === 'credit_note');
        function printCnRegister() {
          const html = `<h1>Credit Note Register — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>Note #</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Ref Invoice</th><th class="r">Taxable</th><th class="r">Tax</th><th class="r">Total</th><th>Status</th></tr></thead><tbody>${cnRows.map(i => `<tr style="color:#dc2626"><td>${i.invoiceNumber ?? ''}</td><td>${i.date}</td><td>${i.partyName}</td><td style="font-size:9px">${i.partyGstin ?? '—'}</td><td>${i.referenceNumber ?? '—'}</td><td class="r">(${fmt(i.subtotal)})</td><td class="r">(${fmt(i.taxTotal)})</td><td class="r b">(${fmt(i.total)})</td><td>${i.status}</td></tr>`).join('')}</tbody><tfoot><tr><td colspan="5" class="b">Total (${cnRows.length})</td><td class="r b">(${fmt(cnRows.reduce((s, i) => s + i.subtotal, 0))})</td><td></td><td class="r b">(${fmt(cnRows.reduce((s, i) => s + i.total, 0))})</td><td></td></tr></tfoot></table>`;
          printHtml(html, `Credit Note Register ${fy}`);
        }
        return (
          <ReportCard
            title={`Credit Note Register  FY ${fy}  (${cnRows.length} notes)`}
            actions={<>
              <Btn k="cncsv" label="⬇ Excel" onClick={() => {
                if (!guardExport()) return;
                const header = ['Note #','Date','Party','GSTIN','Ref Invoice','Taxable','Tax','Total','Status'];
                const lines = cnRows.map(i => [i.invoiceNumber ?? '', i.date, i.partyName, i.partyGstin ?? '', i.referenceNumber ?? '', i.subtotal.toFixed(2), i.taxTotal.toFixed(2), i.total.toFixed(2), i.status].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
                downloadCsv([header.join(','), ...lines].join('\n'), `CreditNotes_${fy}.csv`);
              }} />
              <Btn k="cnpdf" label="🖨 Print" onClick={printCnRegister} />
            </>}
          >
            <table>
              <thead>
                <tr><th>Note #</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Ref Invoice</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>Tax</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {cnRows.length === 0
                  ? <EmptyRow cols={9} msg={`No credit notes in FY ${fy}`} />
                  : cnRows.map(i => (
                    <tr key={i.id} style={{ color: 'var(--danger)' }}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{i.date}</td>
                      <td>{i.partyName}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{i.partyGstin ?? '—'}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{i.referenceNumber ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>({fmtN(i.subtotal)})</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>({fmtN(i.taxTotal)})</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>({fmtN(i.total)})</td>
                      <td><StatusBadge status={i.status} /></td>
                    </tr>
                  ))
                }
              </tbody>
              {cnRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ fontWeight: 700 }}>Total ({cnRows.length})</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>({fmtN(cnRows.reduce((s, i) => s + i.subtotal, 0))})</td>
                    <td />
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>({fmtN(cnRows.reduce((s, i) => s + i.total, 0))})</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </ReportCard>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Debit Note Register                                                 */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'salesPurchases' && activeReport === 'debitNoteRegister' && (() => {
        // Sales-side debit notes (from invoices table) + Purchase-side debit notes (from purchase_invoices table)
        type DnRow = { id: string; invoiceNumber: string; date: string; partyName: string; partyGstin?: string; refInvoice: string; subtotal: number; taxTotal: number; total: number; status: string; source: 'Sales' | 'Purchase' };
        const salesDn: DnRow[] = salesInvoices
          .filter(i => i.docType === 'debit_note')
          .map(i => ({ id: i.id, invoiceNumber: i.invoiceNumber ?? '', date: i.date, partyName: i.partyName, partyGstin: i.partyGstin, refInvoice: i.referenceNumber ?? '—', subtotal: i.subtotal, taxTotal: i.taxTotal, total: i.total, status: i.status, source: 'Sales' as const }));
        const purchDn: DnRow[] = fyPurchases
          .filter(p => p.docType === 'debit_note')
          .map(p => ({ id: p.id, invoiceNumber: p.invoiceNumber ?? '', date: p.date, partyName: p.partyName, partyGstin: p.partyGstin, refInvoice: p.referenceNumber ?? p.supplierInvoice ?? '—', subtotal: p.subtotal, taxTotal: p.taxTotal, total: p.total, status: p.status, source: 'Purchase' as const }));
        const dnRows: DnRow[] = [...salesDn, ...purchDn].sort((a, b) => a.date.localeCompare(b.date));
        function printDnRegister() {
          const html = `<h1>Debit Note Register — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>Note #</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Type</th><th>Ref Invoice</th><th class="r">Taxable</th><th class="r">Tax</th><th class="r">Total</th><th>Status</th></tr></thead><tbody>${dnRows.map(r => `<tr><td>${r.invoiceNumber}</td><td>${r.date}</td><td>${r.partyName}</td><td style="font-size:9px">${r.partyGstin ?? '—'}</td><td>${r.source}</td><td>${r.refInvoice}</td><td class="r">${fmt(r.subtotal)}</td><td class="r">${fmt(r.taxTotal)}</td><td class="r b">${fmt(r.total)}</td><td>${r.status}</td></tr>`).join('')}</tbody><tfoot><tr><td colspan="6" class="b">Total (${dnRows.length})</td><td class="r b">${fmt(dnRows.reduce((s, r) => s + r.subtotal, 0))}</td><td></td><td class="r b">${fmt(dnRows.reduce((s, r) => s + r.total, 0))}</td><td></td></tr></tfoot></table>`;
          printHtml(html, `Debit Note Register ${fy}`);
        }
        return (
          <ReportCard
            title={`Debit Note Register  FY ${fy}  (${dnRows.length} notes)`}
            actions={<>
              <Btn k="dncsv" label="⬇ Excel" onClick={() => {
                if (!guardExport()) return;
                const header = ['Note #','Date','Party','GSTIN','Type','Ref Invoice','Taxable','Tax','Total','Status'];
                const lines = dnRows.map(r => [r.invoiceNumber, r.date, r.partyName, r.partyGstin ?? '', r.source, r.refInvoice, r.subtotal.toFixed(2), r.taxTotal.toFixed(2), r.total.toFixed(2), r.status].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
                downloadCsv([header.join(','), ...lines].join('\n'), `DebitNotes_${fy}.csv`);
              }} />
              <Btn k="dnpdf" label="🖨 Print" onClick={printDnRegister} />
            </>}
          >
            <table>
              <thead>
                <tr><th>Note #</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Type</th><th>Ref Invoice</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>Tax</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {dnRows.length === 0
                  ? <EmptyRow cols={10} msg={`No debit notes in FY ${fy}`} />
                  : dnRows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{r.invoiceNumber || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.date}</td>
                      <td>{r.partyName}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.partyGstin ?? '—'}</td>
                      <td><span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: r.source === 'Purchase' ? '#fef3c7' : '#ede9fe', color: r.source === 'Purchase' ? '#92400e' : '#5b21b6' }}>{r.source}</span></td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{r.refInvoice}</td>
                      <td style={{ textAlign: 'right' }}>{fmtN(r.subtotal)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{fmtN(r.taxTotal)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(r.total)}</td>
                      <td><StatusBadge status={r.status} /></td>
                    </tr>
                  ))
                }
              </tbody>
              {dnRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} style={{ fontWeight: 700 }}>Total ({dnRows.length})</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(dnRows.reduce((s, r) => s + r.subtotal, 0))}</td>
                    <td />
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(dnRows.reduce((s, r) => s + r.total, 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </ReportCard>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Purchase Register                                                  */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'salesPurchases' && activeReport === 'purchaseRegister' && <ReportCard
        title={`Purchase Register  FY ${fy}  (${fyPurchases.length} entries)`}
        actions={<>
          <Btn k="purchcsv" label="⬇ CSV" onClick={() => {
            if (!guardExport()) return;
            const header = ['PI #','Type','Supplier Inv','Date','Supplier','GSTIN','Tax Type','Subtotal','CGST','SGST','IGST','Total Tax','Total','Payment'];
            const lines = fyPurchases.map(p => {
              const dn = p.docType === 'debit_note';
              const s = dn ? -1 : 1;
              const { cgst, sgst, igst } = gstBreakdown(p.taxType, p.taxTotal);
              return [p.invoiceNumber ?? '', dn ? 'Debit Note' : 'Purchase', p.supplierInvoice ?? '', p.date, p.partyName, p.partyGstin ?? '', p.taxType, (s * p.subtotal).toFixed(2), (s * cgst).toFixed(2), (s * sgst).toFixed(2), (s * igst).toFixed(2), (s * p.taxTotal).toFixed(2), (s * p.total).toFixed(2), p.paymentStatus ?? 'unpaid'].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
            });
            downloadCsv([header.join(','), ...lines].join('\n'), `PurchaseRegister_${fy}.csv`);
          }} />
          <Btn k="purchpdf" label="🖨 Print" onClick={printPurchaseRegister} />
        </>}
      >
        <table>
          <thead>
            <tr><th>PI #</th><th>Type</th><th>Supplier Inv</th><th>Date</th><th>Supplier</th><th>Tax Type</th><th style={{ textAlign: 'right' }}>Subtotal</th><th style={{ textAlign: 'right' }}>CGST</th><th style={{ textAlign: 'right' }}>SGST</th><th style={{ textAlign: 'right' }}>IGST</th><th style={{ textAlign: 'right' }}>Total</th><th>Payment</th></tr>
          </thead>
          <tbody>
            {fyPurchases.length === 0
              ? <EmptyRow cols={12} msg={`No purchase invoices in FY ${fy}`} />
              : fyPurchases.map(p => {
                const dn = p.docType === 'debit_note';
                const { cgst, sgst, igst } = gstBreakdown(p.taxType, p.taxTotal);
                const amtColor = dn ? '#d97706' : undefined;
                const prefix = dn ? '−' : '';
                return (
                  <tr key={p.id} style={{ background: dn ? '#fffbeb' : undefined }}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: dn ? '#d97706' : undefined }}>{p.invoiceNumber ?? '—'}</td>
                    <td><span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: dn ? '#fef3c7' : '#f0fdf4', color: dn ? '#92400e' : '#15803d' }}>{dn ? 'Debit Note' : 'Purchase'}</span></td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.supplierInvoice ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{p.date}</td>
                    <td>{p.partyName}</td>
                    <td style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>{p.taxType}</td>
                    <td style={{ textAlign: 'right', color: amtColor }}>{prefix}{fmtN(p.subtotal)}</td>
                    <td style={{ textAlign: 'right', color: amtColor ?? 'var(--text-muted)' }}>{cgst > 0 ? `${prefix}${fmtN(cgst)}` : '—'}</td>
                    <td style={{ textAlign: 'right', color: amtColor ?? 'var(--text-muted)' }}>{sgst > 0 ? `${prefix}${fmtN(sgst)}` : '—'}</td>
                    <td style={{ textAlign: 'right', color: amtColor ?? 'var(--text-muted)' }}>{igst > 0 ? `${prefix}${fmtN(igst)}` : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: amtColor }}>{prefix}{fmtN(p.total)}</td>
                    <td><StatusBadge status={p.paymentStatus ?? 'unpaid'} /></td>
                  </tr>
                );
              })
            }
          </tbody>
          {fyPurchases.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6} style={{ fontWeight: 700 }}>Net Total ({fyPurchases.length})</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(fyPurchases.reduce((s, p) => s + (p.docType === 'debit_note' ? -p.subtotal : p.subtotal), 0))}</td>
                <td colSpan={3} />
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(fyPurchases.reduce((s, p) => s + (p.docType === 'debit_note' ? -p.total : p.total), 0))}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* HSN Summary                                                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'gst' && activeReport === 'hsnSummary' && <ReportCard
        title={`HSN Summary — GSTR-1 Table 12  (FY ${fy})`}
        actions={<>
          <Btn k="hsncsv" label="⬇ CSV" onClick={() => {
            if (!guardExport()) return;
            const header = ['HSN/SAC','Qty','UOM','Taxable Value','CGST','SGST','IGST','Total'];
            const lines = hsnRows.map(r => [r.hsnSac || 'NO_HSN', r.qty.toFixed(2), r.unit, r.taxable.toFixed(2), r.cgst.toFixed(2), r.sgst.toFixed(2), r.igst.toFixed(2), r.total.toFixed(2)].map(v => `"${v}"`).join(','));
            downloadCsv([header.join(','), ...lines].join('\n'), `HSN_Summary_${fy}.csv`);
          }} />
          <Btn k="hsnpdf" label="🖨 Print" onClick={() => {
            const totTaxable = hsnRows.reduce((s, r) => s + r.taxable, 0);
            const rowsHtml = hsnRows.map(r => `<tr><td>${r.hsnSac || 'NO_HSN'}</td><td class="r">${r.qty}</td><td>${r.unit}</td><td class="r">${fmt(r.taxable)}</td><td class="r">${r.cgst > 0 ? fmt(r.cgst) : '—'}</td><td class="r">${r.sgst > 0 ? fmt(r.sgst) : '—'}</td><td class="r">${r.igst > 0 ? fmt(r.igst) : '—'}</td><td class="r b">${fmt(r.total)}</td></tr>`).join('');
            printHtml(`<h1>HSN Summary — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>HSN/SAC</th><th class="r">Qty</th><th>UOM</th><th class="r">Taxable</th><th class="r">CGST</th><th class="r">SGST</th><th class="r">IGST</th><th class="r">Total</th></tr></thead><tbody>${rowsHtml}</tbody><tfoot><tr><td colspan="3" class="b">Total</td><td class="r b">${fmt(totTaxable)}</td><td colspan="3"></td><td class="r b">${fmt(hsnRows.reduce((s, r) => s + r.total, 0))}</td></tr></tfoot></table>`, `HSN Summary ${fy}`);
          }} />
        </>}
      >
        <table>
          <thead>
            <tr><th>HSN/SAC</th><th style={{ textAlign: 'right' }}>Qty</th><th>UOM</th><th style={{ textAlign: 'right' }}>Taxable Value</th><th style={{ textAlign: 'right' }}>CGST</th><th style={{ textAlign: 'right' }}>SGST</th><th style={{ textAlign: 'right' }}>IGST</th><th style={{ textAlign: 'right' }}>Total</th></tr>
          </thead>
          <tbody>
            {hsnRows.length === 0
              ? <EmptyRow cols={8} msg="No invoice data for this period" />
              : hsnRows.map(r => (
                <tr key={r.hsnSac}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.hsnSac || 'NO_HSN'}</td>
                  <td style={{ textAlign: 'right' }}>{r.qty}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{r.unit}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtN(r.taxable)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{r.cgst > 0 ? fmtN(r.cgst) : '—'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{r.sgst > 0 ? fmtN(r.sgst) : '—'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{r.igst > 0 ? fmtN(r.igst) : '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(r.total)}</td>
                </tr>
              ))
            }
          </tbody>
          {hsnRows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={3} style={{ fontWeight: 700 }}>Grand Total</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(hsnRows.reduce((s, r) => s + r.taxable, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(hsnRows.reduce((s, r) => s + r.cgst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(hsnRows.reduce((s, r) => s + r.sgst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(hsnRows.reduce((s, r) => s + r.igst, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(hsnRows.reduce((s, r) => s + r.total, 0))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* P&L Statement                                                      */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'financial' && activeReport === 'pnl' && <FeatureGate feature="profitLoss">
      <ReportCard
        title={`Profit & Loss Statement  FY ${fy}`}
        actions={<>
          <Btn k="plcsv" label="⬇ Excel" onClick={() => {
            if (!guardExport()) return;
            const rows: string[][] = [
              ['Profit & Loss Statement', `FY ${fy}`, '', ''],
              ['Particulars', 'Amount (₹)', '', ''],
              ['Revenue (taxable)', plSalesRevenue.toFixed(2), '', ''],
              ['Less: Credit Notes (Sales Returns)', (-plCreditNotes).toFixed(2), '', ''],
              ['Net Revenue', plRevenue.toFixed(2), '', ''],
              ['GST Collected', plGst.toFixed(2), '', ''],
              ['Opening Stock', openingStockValue.toFixed(2), '', ''],
              ['Add: Purchases', plPurchases.toFixed(2), '', ''],
              ['Less: Closing Stock', (-closingStockValue).toFixed(2), '', ''],
              ['Cost of Goods Sold (COGS)', (-plCogs).toFixed(2), '', ''],
              ['--- Direct Expenses ---', '', '', ''],
              ...Object.entries(plExpByCategory).filter(([c]) => DIRECT_EXP_CATS.has(c)).map(([cat, amt]) => [cat.replace(/_/g, ' '), (-amt).toFixed(2), '', '']),
              ['Gross Profit', plGrossProfit.toFixed(2), '', ''],
              ['--- Indirect Expenses ---', '', '', ''],
              ...Object.entries(plExpByCategory).filter(([c]) => !DIRECT_EXP_CATS.has(c)).map(([cat, amt]) => [cat.replace(/_/g, ' '), (-amt).toFixed(2), '', '']),
              ['Finance Cost (Interest on Loans)', (-plFinanceCost).toFixed(2), '', ''],
              ['Other Income (Interest)', plOtherIncome.toFixed(2), '', ''],
              ['Net Profit / (Loss)', plNetProfit.toFixed(2), '', ''],
            ];
            downloadCsv(rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n'), `ProfitLoss_${fy}.csv`);
          }} />
          <Btn k="plpdf" label="🖨 Print" onClick={() => {
          const directExpRows = Object.entries(plExpByCategory).filter(([c]) => DIRECT_EXP_CATS.has(c)).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => `<tr><td style="padding-left:1.5rem">${cat.replace(/_/g, ' ')}</td><td class="r red">${fmt(amt)}</td></tr>`).join('');
          const indirectExpRows = Object.entries(plExpByCategory).filter(([c]) => !DIRECT_EXP_CATS.has(c)).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => `<tr><td style="padding-left:1.5rem">${cat.replace(/_/g, ' ')}</td><td class="r red">${fmt(amt)}</td></tr>`).join('');
          printHtml(`<h1>P&L — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>Particulars</th><th class="r">Amount</th></tr></thead><tbody><tr><td class="b">Revenue (taxable)</td><td class="r">${fmt(plSalesRevenue)}</td></tr><tr><td style="padding-left:1.5rem;color:#dc2626">Less: Credit Notes (Sales Returns)</td><td class="r red">(${fmt(plCreditNotes)})</td></tr><tr style="background:#f8fafc;font-weight:700"><td>Net Revenue</td><td class="r">${fmt(plRevenue)}</td></tr><tr><td class="b">Cost of Goods Sold</td><td></td></tr><tr><td style="padding-left:1.5rem">Opening Stock</td><td class="r">${fmt(openingStockValue)}</td></tr><tr><td style="padding-left:1.5rem">Add: Purchases</td><td class="r">${fmt(plPurchases)}</td></tr><tr><td style="padding-left:1.5rem">Less: Closing Stock</td><td class="r red">(${fmt(closingStockValue)})</td></tr><tr style="background:#f8fafc;font-weight:700"><td>Cost of Goods Sold (COGS)</td><td class="r red">${fmt(plCogs)}</td></tr><tr><td class="b">Direct Expenses</td><td></td></tr>${directExpRows}<tr style="background:#f8fafc;font-weight:700"><td>Gross Profit</td><td class="r ${plGrossProfit >= 0 ? 'green' : 'red'}">${fmt(plGrossProfit)}</td></tr><tr><td class="b">Indirect Expenses</td><td></td></tr>${indirectExpRows}<tr><td>Finance Cost (Interest on Loans)</td><td class="r red">(${fmt(plFinanceCost)})</td></tr><tr><td>Other Income (Interest)</td><td class="r green">${fmt(plOtherIncome)}</td></tr><tr style="background:#1e293b;color:white;font-weight:800"><td>Net Profit / (Loss)</td><td class="r">${fmt(plNetProfit)}</td></tr></tbody></table>`, `P&L ${fy}`);
        }} />
        </>}
      >
        <div className="form-row form-row-2" style={{ gap: 0, marginBottom: 0 }}>
          {/* Left: P&L lines */}
          <div style={{ borderRight: '1px solid var(--border)', padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', marginBottom: '0.75rem' }}>Income & Costs</div>
            {[
              { label: 'Revenue (taxable)', val: plSalesRevenue, bold: false, color: 'var(--success)' },
              { label: 'Less: Credit Notes (Returns)', val: -plCreditNotes, bold: false, color: 'var(--danger)' },
              { label: 'Net Revenue', val: plRevenue, bold: true, color: 'var(--text-1)' },
              { label: 'GST Collected', val: plGst, bold: false, color: 'var(--text-2)' },
              { label: 'Opening Stock', val: openingStockValue, bold: false, color: 'var(--text-2)' },
              { label: 'Add: Purchases', val: plPurchases, bold: false, color: 'var(--text-2)' },
              { label: 'Less: Closing Stock', val: -closingStockValue, bold: false, color: 'var(--success)' },
              { label: 'Cost of Goods Sold (COGS)', val: -plCogs, bold: true, color: 'var(--danger)' },
              { label: 'Direct Expenses', val: -plDirectExp, bold: false, color: 'var(--danger)' },
              { label: 'Gross Profit', val: plGrossProfit, bold: true, color: plGrossProfit >= 0 ? 'var(--success)' : 'var(--danger)' },
              { label: 'Indirect Expenses', val: -plIndirectExp, bold: false, color: 'var(--danger)' },
              { label: 'Finance Cost (Interest on Loans)', val: -plFinanceCost, bold: false, color: 'var(--danger)' },
              { label: 'Other Income (Interest)', val: plOtherIncome, bold: false, color: 'var(--success)' },
              { label: 'Net Profit / (Loss)', val: plNetProfit, bold: true, color: plNetProfit >= 0 ? 'var(--success)' : 'var(--danger)' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: r.bold ? '0.45rem 0' : '0.28rem 0', borderBottom: '1px solid var(--border)', borderTop: r.bold ? '1px solid var(--border)' : undefined, marginTop: r.bold ? '0.1rem' : undefined }}>
                <span style={{ fontWeight: r.bold ? 700 : 400, fontSize: r.bold ? '0.85rem' : '0.82rem', color: 'var(--text-2)' }}>{r.label}</span>
                <span style={{ fontWeight: r.bold ? 700 : 600, fontSize: r.bold ? '0.9rem' : '0.82rem', color: r.color }}>
                  {r.val < 0 ? '−' : ''}{fmtN(Math.abs(r.val))}
                </span>
              </div>
            ))}
          </div>
          {/* Right: expense breakdown */}
          <div style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-2)', marginBottom: '0.75rem' }}>Expense Breakdown  ({fyExpenses.length} entries)</div>
            {Object.entries(plExpByCategory).length === 0
              ? <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>No expenses in FY {fy}</div>
              : Object.entries(plExpByCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                const pct = plTotalExp > 0 ? amt / plTotalExp * 100 : 0;
                return (
                  <div key={cat} style={{ marginBottom: '0.45rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.15rem' }}>
                      <span style={{ textTransform: 'capitalize', color: 'var(--text-2)' }}>{cat.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: 600 }}>{fmtN(amt)} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div style={{ background: 'var(--border)', borderRadius: 999, height: 4 }}>
                      <div style={{ background: 'var(--primary)', borderRadius: 999, height: 4, width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })
            }
            <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', fontWeight: 700 }}>
              <span>Total Expenses</span><span style={{ color: 'var(--danger)' }}>{fmtN(plTotalExp)}</span>
            </div>
          </div>
        </div>
      </ReportCard>
      </FeatureGate>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Balance Sheet                                                      */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'financial' && activeReport === 'balanceSheet' && <FeatureGate feature="balanceSheet">
      <ReportCard
        title="Balance Sheet"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>As of:</label>
            <input type="date" value={bsAsOf} onChange={e => setBsAsOf(e.target.value)}
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)' }} />
          </div>
        }
      >
        {balanceSheet ? (
          <div className="form-row form-row-2" style={{ gap: '2rem', padding: '0.5rem 0', marginBottom: 0 }}>
            {/* Assets */}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--primary)', marginBottom: '0.75rem' }}>Assets</div>
              {[
                { label: 'Cash Received', val: balanceSheet.assets.cash },
                { label: 'Receivables (Outstanding)', val: balanceSheet.assets.receivables },
                { label: 'Stock Value', val: balanceSheet.assets.stock },
                { label: 'Fixed Assets', val: balanceSheet.assets.fixedAssets },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border-light)', fontSize: '0.875rem' }}>
                  <span style={{ color: 'var(--text-2)' }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>₹{val.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontWeight: 700, fontSize: '1rem', borderTop: '2px solid var(--primary)', marginTop: '0.25rem' }}>
                <span>Total Assets</span>
                <span style={{ color: 'var(--primary)' }}>₹{balanceSheet.assets.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            {/* Liabilities + Equity */}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--danger)', marginBottom: '0.75rem' }}>Liabilities</div>
              {[
                { label: 'Payables (Outstanding)', val: balanceSheet.liabilities.payables },
                { label: 'GST Payable (Net)', val: balanceSheet.liabilities.gstPayable },
                { label: 'Secured Loans', val: balanceSheet.liabilities.securedLoans },
                { label: 'Unsecured Loans', val: balanceSheet.liabilities.unsecuredLoans },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border-light)', fontSize: '0.875rem' }}>
                  <span style={{ color: 'var(--text-2)' }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>₹{val.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border-light)', fontWeight: 700, fontSize: '0.875rem' }}>
                <span>Total Liabilities</span>
                <span>₹{balanceSheet.liabilities.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--success)', margin: '1rem 0 0.75rem' }}>Equity</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border-light)', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--text-2)' }}>Net Profit / (Loss)</span>
                <span style={{ fontWeight: 500, color: balanceSheet.equity.netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  ₹{Math.abs(balanceSheet.equity.netProfit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}{balanceSheet.equity.netProfit < 0 ? ' (Loss)' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid var(--border-light)', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--text-2)' }}>Capital Account</span>
                <span style={{ fontWeight: 500 }}>₹{balanceSheet.equity.capitalAccount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontWeight: 700, fontSize: '1rem', borderTop: '2px solid var(--danger)', marginTop: '0.25rem' }}>
                <span>Liabilities + Equity</span>
                <span style={{ color: 'var(--danger)' }}>₹{balanceSheet.liabilitiesPlusEquity.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Loading balance sheet…</div>
        )}
      </ReportCard>
      </FeatureGate>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Receivables Ageing                                                 */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'moneyInOut' && activeReport === 'receivablesAgeing' && <ReportCard
        title={`Receivables Ageing — As of ${today()}  (${debtorRows.length} outstanding)`}
        actions={<>
          <Btn k="ageingcsv" label="⬇ CSV" onClick={() => {
            if (!guardExport()) return;
            const header = ['Invoice #','Date','Due Date','Party','GSTIN','Total','Paid','Outstanding','Days Overdue','Bucket'];
            const lines = debtorRows.map(r => [r.invoiceNumber ?? '', r.date, r.dueDate ?? '', r.partyName, r.partyGstin ?? '', r.total.toFixed(2), (r.paidAmount ?? 0).toFixed(2), r.outstanding.toFixed(2), String(r.days), r.bucket].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
            downloadCsv([header.join(','), ...lines].join('\n'), `Ageing_${today()}.csv`);
          }} />
          <Btn k="ageingpdf" label="🖨 Print" onClick={() => {
            const bucketHtml = (label: string, rows: typeof debtorRows, color: string) => rows.length ? `<tr style="background:#f8fafc"><td colspan="5" class="b" style="color:${color}">${label} (${rows.length})</td><td class="r b" style="color:${color}">${fmt(rows.reduce((s, r) => s + r.outstanding, 0))}</td></tr>${rows.map(r => `<tr><td>${r.invoiceNumber ?? ''}</td><td>${r.date}</td><td>${r.dueDate ?? '—'}</td><td>${r.partyName}</td><td class="r">${r.days} d</td><td class="r b" style="color:${color}">${fmt(r.outstanding)}</td></tr>`).join('')}` : '';
            const html = `<h1>Receivables Ageing — ${profile?.name ?? ''}</h1><div class="sub">As of ${today()}</div><table><thead><tr><th>Invoice #</th><th>Date</th><th>Due Date</th><th>Party</th><th class="r">Days</th><th class="r">Outstanding</th></tr></thead><tbody>${bucketHtml('Current', debtorRows.filter(r => r.bucket === 'Current'), '#16a34a')}${bucketHtml('1–30 days', debtorRows.filter(r => r.bucket === '1–30 d'), '#d97706')}${bucketHtml('31–60 days', debtorRows.filter(r => r.bucket === '31–60 d'), '#ea580c')}${bucketHtml('61–90 days', debtorRows.filter(r => r.bucket === '61–90 d'), '#dc2626')}${bucketHtml('90+ days', debtorRows.filter(r => r.bucket === '90+ d'), '#7f1d1d')}</tbody><tfoot><tr><td colspan="5" class="b">Grand Total</td><td class="r b">${fmt(debtorRows.reduce((s, r) => s + r.outstanding, 0))}</td></tr></tfoot></table>`;
            printHtml(html, `Ageing Report ${today()}`);
          }} />
        </>}
      >
        {/* Bucket KPIs */}
        <div style={{ display: 'flex', gap: '1px', background: 'var(--border)', borderBottom: '1px solid var(--border)' }}>
          {[{ label: 'Current', key: 'Current', color: 'var(--success)' }, { label: '1–30 d', key: '1–30 d', color: 'var(--warning)' }, { label: '31–60 d', key: '31–60 d', color: '#ea580c' }, { label: '61–90 d', key: '61–90 d', color: 'var(--danger)' }, { label: '90+ d', key: '90+ d', color: '#7f1d1d' }].map(b => {
            const rows = debtorRows.filter(r => r.bucket === b.key);
            const total = rows.reduce((s, r) => s + r.outstanding, 0);
            return (
              <div key={b.label} style={{ flex: 1, background: 'var(--surface)', padding: '0.65rem 0.5rem', textAlign: 'center', minWidth: 90 }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{b.label}</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: rows.length ? b.color : 'var(--text-muted)' }}>{rows.length ? fmtN(total) : '—'}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{rows.length} inv</div>
              </div>
            );
          })}
        </div>
        <table>
          <thead><tr><th>Invoice #</th><th>Date</th><th>Due Date</th><th>Party</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Paid</th><th style={{ textAlign: 'right' }}>Outstanding</th><th style={{ textAlign: 'right' }}>Days</th><th>Bucket</th></tr></thead>
          <tbody>
            {debtorRows.length === 0
              ? <EmptyRow cols={9} msg="No outstanding receivables — all clear!" />
              : debtorRows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{r.invoiceNumber ?? '—'}</td>
                  <td>{r.date}</td>
                  <td style={{ color: r.days > 0 ? 'var(--danger)' : 'var(--text-2)' }}>{r.dueDate ?? '—'}</td>
                  <td>{r.partyName}</td>
                  <td style={{ textAlign: 'right' }}>{fmtN(r.total)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)' }}>{(r.paidAmount ?? 0) > 0 ? fmtN(r.paidAmount ?? 0) : '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: r.color }}>{fmtN(r.outstanding)}</td>
                  <td style={{ textAlign: 'right', color: r.days > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>{r.days > 0 ? r.days : '—'}</td>
                  <td><AgeBadge bucket={r.bucket} color={r.color} /></td>
                </tr>
              ))
            }
          </tbody>
          {debtorRows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6} style={{ fontWeight: 700 }}>Total Outstanding</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{fmtN(debtorRows.reduce((s, r) => s + r.outstanding, 0))}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Cash Ledger                                                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'moneyInOut' && activeReport === 'cashLedger' && <ReportCard
        title={`Cash Ledger  FY ${fy}  (${cashEntries.length} transactions)`}
        actions={<>
          <Btn k="cashcsv" label="⬇ Excel" onClick={() => {
            if (!guardExport()) return;
            let bal = 0;
            const header = ['Date','Description','Cash In (₹)','Cash Out (₹)','Balance (₹)'];
            const lines = cashEntries.map(e => { bal += e.in - e.out; return [e.date, e.desc, e.in > 0 ? e.in.toFixed(2) : '', e.out > 0 ? e.out.toFixed(2) : '', bal.toFixed(2)].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','); });
            downloadCsv([header.join(','), ...lines].join('\n'), `CashLedger_${fy}.csv`);
          }} />
          <Btn k="cashpdf" label="🖨 Print" onClick={() => {
            let bal = 0;
            const rows = cashEntries.map(e => { bal += e.in - e.out; return `<tr><td>${e.date}</td><td>${e.desc}</td><td class="r green">${e.in > 0 ? fmt(e.in) : '—'}</td><td class="r red">${e.out > 0 ? fmt(e.out) : '—'}</td><td class="r b">${fmt(bal)}</td></tr>`; }).join('');
            const totIn = cashEntries.reduce((s, e) => s + e.in, 0); const totOut = cashEntries.reduce((s, e) => s + e.out, 0);
            printHtml(`<h1>Cash Ledger — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>Date</th><th>Description</th><th class="r">Cash In</th><th class="r">Cash Out</th><th class="r">Balance</th></tr></thead><tbody>${rows || '<tr><td colspan="5" style="text-align:center;color:#94a3b8">No cash transactions</td></tr>'}</tbody><tfoot><tr><td colspan="2" class="b">Total</td><td class="r b green">${fmt(totIn)}</td><td class="r b red">${fmt(totOut)}</td><td class="r b">${fmt(totIn - totOut)}</td></tr></tfoot></table>`, `Cash Ledger ${fy}`);
          }} />
        </>}
      >
        <table>
          <thead><tr><th>Date</th><th>Description</th><th style={{ textAlign: 'right' }}>Cash In</th><th style={{ textAlign: 'right' }}>Cash Out</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
          <tbody>
            {cashEntries.length === 0
              ? <EmptyRow cols={5} msg="No cash transactions in this period" />
              : (() => {
                let balance = 0;
                return cashEntries.map((e, idx) => {
                  balance += e.in - e.out;
                  return (
                    <tr key={idx}>
                      <td style={{ whiteSpace: 'nowrap' }}>{e.date}</td>
                      <td style={{ fontSize: '0.82rem' }}>{e.desc}</td>
                      <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: e.in > 0 ? 600 : 400 }}>{e.in > 0 ? fmtN(e.in) : '—'}</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: e.out > 0 ? 600 : 400 }}>{e.out > 0 ? fmtN(e.out) : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: balance >= 0 ? 'var(--text-1)' : 'var(--danger)' }}>{fmtN(balance)}</td>
                    </tr>
                  );
                });
              })()
            }
          </tbody>
          {cashEntries.length > 0 && (() => {
            const totIn = cashEntries.reduce((s, e) => s + e.in, 0);
            const totOut = cashEntries.reduce((s, e) => s + e.out, 0);
            return (
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ fontWeight: 700 }}>Total</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{fmtN(totIn)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{fmtN(totOut)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(totIn - totOut)}</td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Creditor / Payables Ageing                                         */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'moneyInOut' && activeReport === 'payablesAgeing' && <ReportCard
        title={`Creditor / Payables Ageing — As of ${today()}  (${creditorRows.length} outstanding)`}
        actions={<Btn k="credpdf" label="🖨 Print" onClick={() => {
          const bucketHtml = (label: string, rows: typeof creditorRows, color: string) => rows.length ? `<tr style="background:#f8fafc"><td colspan="4" class="b" style="color:${color}">${label} (${rows.length})</td><td class="r b" style="color:${color}">${fmt(rows.reduce((s, r) => s + r.total, 0))}</td></tr>${rows.map(r => `<tr><td>${r.ref}</td><td>${r.date}</td><td>${r.dueDate ?? '—'}</td><td>${r.partyName} <span style="font-size:9px;color:#64748b">[${r.source}]</span></td><td class="r b" style="color:${color}">${fmt(r.total)}</td></tr>`).join('')}` : '';
          printHtml(`<h1>Creditors Ageing — ${profile?.name ?? ''}</h1><div class="sub">As of ${today()} (Purchases + Expenses on Credit)</div><table><thead><tr><th>Ref #</th><th>Date</th><th>Due Date</th><th>Payee</th><th class="r">Payable</th></tr></thead><tbody>${['Current','1–30 d','31–60 d','61–90 d','90+ d'].map((b, i) => bucketHtml(b, creditorRows.filter(r => r.bucket === b), ['#16a34a','#d97706','#ea580c','#dc2626','#7f1d1d'][i])).join('')}</tbody><tfoot><tr><td colspan="4" class="b">Grand Total</td><td class="r b">${fmt(creditorRows.reduce((s, r) => s + r.total, 0))}</td></tr></tfoot></table>`, `Creditors Ageing ${today()}`);
        }} />}
      >
        <div style={{ display: 'flex', gap: '1px', background: 'var(--border)', borderBottom: '1px solid var(--border)' }}>
          {[{ label: 'Current', key: 'Current', color: 'var(--success)' }, { label: '1–30 d', key: '1–30 d', color: 'var(--warning)' }, { label: '31–60 d', key: '31–60 d', color: '#ea580c' }, { label: '61–90 d', key: '61–90 d', color: 'var(--danger)' }, { label: '90+ d', key: '90+ d', color: '#7f1d1d' }].map(b => {
            const rows = creditorRows.filter(r => r.bucket === b.key);
            return (
              <div key={b.label} style={{ flex: 1, background: 'var(--surface)', padding: '0.65rem 0.5rem', textAlign: 'center', minWidth: 90 }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{b.label}</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: rows.length ? b.color : 'var(--text-muted)' }}>{rows.length ? fmtN(rows.reduce((s, r) => s + r.total, 0)) : '—'}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{rows.length} entries</div>
              </div>
            );
          })}
        </div>
        <table>
          <thead><tr><th>Ref #</th><th>Date</th><th>Due Date</th><th>Payee / Vendor</th><th>Type</th><th style={{ textAlign: 'right' }}>Payable</th><th>Status</th><th style={{ textAlign: 'right' }}>Days</th><th>Bucket</th></tr></thead>
          <tbody>
            {creditorRows.length === 0
              ? <EmptyRow cols={9} msg="No outstanding payables — all clear!" />
              : creditorRows.map(r => (
                <tr key={r.id} style={r.source === 'expense' ? { background: 'var(--row-tint-yellow)' } : undefined}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{r.ref || '—'}</td>
                  <td>{r.date}</td>
                  <td style={{ color: r.days > 0 ? 'var(--danger)' : 'var(--text-2)' }}>{r.dueDate ?? '—'}</td>
                  <td>{r.partyName}</td>
                  <td><span className={`badge ${r.source === 'purchase' ? 'badge-blue' : 'badge-gray'}`} style={{ fontSize: '0.68rem' }}>{r.source}</span></td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: r.color }}>{fmtN(r.total)}</td>
                  <td><StatusBadge status={r.paymentStatus} /></td>
                  <td style={{ textAlign: 'right', color: r.days > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>{r.days > 0 ? r.days : '—'}</td>
                  <td><AgeBadge bucket={r.bucket} color={r.color} /></td>
                </tr>
              ))
            }
          </tbody>
          {creditorRows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} style={{ fontWeight: 700 }}>Total Payable</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{fmtN(creditorRows.reduce((s, r) => s + r.total, 0))}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* TDS / TCS                                                          */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'tax' && activeReport === 'tdsTcs' && <ReportCard
        title={`TDS / TCS Deductions  FY ${fy}  (${tdsRows.length} invoices)`}
        actions={<>
          <Btn k="tdscsv" label="⬇ CSV" onClick={() => {
            if (!guardExport()) return;
            const header = ['Invoice #','Date','Party','GSTIN','Taxable','TDS Rate %','TDS Section','TDS Amt','TCS Rate %','TCS Amt','Net Payable'];
            const lines = tdsRows.map(i => { const tdsAmt = i.tdsRate ? i.subtotal * i.tdsRate / 100 : 0; const tcsAmt = i.tcsRate ? i.subtotal * i.tcsRate / 100 : 0; return [i.invoiceNumber ?? '', i.date, i.partyName, i.partyGstin ?? '', i.subtotal.toFixed(2), (i.tdsRate ?? 0).toFixed(2), i.tdsSection ?? '', tdsAmt.toFixed(2), (i.tcsRate ?? 0).toFixed(2), tcsAmt.toFixed(2), (i.total - tdsAmt + tcsAmt).toFixed(2)].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','); });
            downloadCsv([header.join(','), ...lines].join('\n'), `TDS_TCS_${fy}.csv`);
          }} />
          <Btn k="tdspdf" label="🖨 Print" onClick={() => {
            const totTds = tdsRows.reduce((s, i) => s + (i.tdsRate ? i.subtotal * i.tdsRate / 100 : 0), 0);
            const rows = tdsRows.map(i => { const tdsAmt = i.tdsRate ? i.subtotal * i.tdsRate / 100 : 0; const tcsAmt = i.tcsRate ? i.subtotal * i.tcsRate / 100 : 0; return `<tr><td>${i.invoiceNumber ?? ''}</td><td>${i.date}</td><td>${i.partyName}</td><td class="r">${fmt(i.subtotal)}</td><td class="r">${i.tdsRate ? `${i.tdsRate}%` : '—'}</td><td class="r red">${tdsAmt > 0 ? fmt(tdsAmt) : '—'}</td><td class="r">${i.tcsRate ? `${i.tcsRate}%` : '—'}</td><td class="r orange">${tcsAmt > 0 ? fmt(tcsAmt) : '—'}</td></tr>`; }).join('');
            printHtml(`<h1>TDS/TCS — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>Invoice #</th><th>Date</th><th>Party</th><th class="r">Taxable</th><th class="r">TDS %</th><th class="r">TDS Amt</th><th class="r">TCS %</th><th class="r">TCS Amt</th></tr></thead><tbody>${rows || '<tr><td colspan="8" style="text-align:center;color:#94a3b8">No TDS/TCS invoices</td></tr>'}</tbody><tfoot><tr><td colspan="5" class="b">Total TDS</td><td class="r b red">${fmt(totTds)}</td><td colspan="2"></td></tr></tfoot></table>`, `TDS TCS ${fy}`);
          }} />
        </>}
      >
        <table>
          <thead><tr><th>Invoice #</th><th>Date</th><th>Party</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>TDS Rate</th><th>Section</th><th style={{ textAlign: 'right' }}>TDS Amt</th><th style={{ textAlign: 'right' }}>TCS Rate</th><th style={{ textAlign: 'right' }}>TCS Amt</th><th style={{ textAlign: 'right' }}>Net Payable</th></tr></thead>
          <tbody>
            {tdsRows.length === 0
              ? <EmptyRow cols={10} msg="No TDS/TCS deductions found in this period" />
              : tdsRows.map(i => {
                const tdsAmt = i.tdsRate ? i.subtotal * i.tdsRate / 100 : 0;
                const tcsAmt = i.tcsRate ? i.subtotal * i.tcsRate / 100 : 0;
                return (
                  <tr key={i.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{i.invoiceNumber ?? '—'}</td>
                    <td>{i.date}</td><td>{i.partyName}</td>
                    <td style={{ textAlign: 'right' }}>{fmtN(i.subtotal)}</td>
                    <td style={{ textAlign: 'right' }}>{i.tdsRate ? `${i.tdsRate}%` : '—'}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{i.tdsSection ?? '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{tdsAmt > 0 ? fmtN(tdsAmt) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{i.tcsRate ? `${i.tcsRate}%` : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--warning)' }}>{tcsAmt > 0 ? fmtN(tcsAmt) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(i.total - tdsAmt + tcsAmt)}</td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* TDS Payable Report (Purchases + Expenses, section-wise)            */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'tax' && activeReport === 'tdsPayable' && (() => {
        type TdsEntry = { date: string; partyOrVendor: string; description: string; baseAmount: number; tdsSection: string; tdsRate: number; tdsAmt: number; source: 'purchase' | 'expense' | 'loan_interest' };
        const tdsPayableRows: TdsEntry[] = [
          ...fyPurchases.filter(p => p.tdsSection && (p.tdsRate ?? 0) > 0).map(p => ({
            date: p.date,
            partyOrVendor: p.partyName,
            description: p.invoiceNumber,
            baseAmount: p.subtotal,
            tdsSection: p.tdsSection!,
            tdsRate: p.tdsRate!,
            tdsAmt: p.subtotal * p.tdsRate! / 100,
            source: 'purchase' as const,
          })),
          ...fyExpenses.filter(e => e.tdsSection && (e.tdsRate ?? 0) > 0).map(e => ({
            date: e.date,
            partyOrVendor: e.vendor ?? e.description,
            description: e.description,
            baseAmount: e.amount,
            tdsSection: e.tdsSection!,
            tdsRate: e.tdsRate!,
            tdsAmt: e.amount * e.tdsRate! / 100,
            source: 'expense' as const,
          })),
          // TDS on loan interest (Section 194A) — applies to the interest
          // portion only, never principal, since TDS is on income the
          // *lender* earns, not the loan capital itself.
          ...fyLoanTransactions.filter(t => t.type === 'repayment' && t.tdsSection && (t.tdsRate ?? 0) > 0 && t.interestAmount).map(t => ({
            date: t.date,
            partyOrVendor: loanAccountsById.get(t.loanAccountId)?.lenderName ?? 'Unknown Lender',
            description: 'Loan interest (EMI)',
            baseAmount: t.interestAmount!,
            tdsSection: t.tdsSection!,
            tdsRate: t.tdsRate!,
            tdsAmt: t.interestAmount! * t.tdsRate! / 100,
            source: 'loan_interest' as const,
          })),
        ].sort((a, b) => a.date.localeCompare(b.date));

        // section-wise summary
        type SecSum = { section: string; totalBase: number; totalTds: number; count: number };
        const secMap = new Map<string, SecSum>();
        for (const r of tdsPayableRows) {
          const s = secMap.get(r.tdsSection) ?? { section: r.tdsSection, totalBase: 0, totalTds: 0, count: 0 };
          s.totalBase += r.baseAmount;
          s.totalTds += r.tdsAmt;
          s.count += 1;
          secMap.set(r.tdsSection, s);
        }
        const secSummary = [...secMap.values()].sort((a, b) => a.section.localeCompare(b.section));
        const grandTds = tdsPayableRows.reduce((s, r) => s + r.tdsAmt, 0);

        function printTdsPayable() {
          const rows = tdsPayableRows.map(r => `<tr><td>${r.date}</td><td>${r.partyOrVendor}</td><td>${r.description}</td><td class="r">${fmt(r.baseAmount)}</td><td>${r.tdsSection}</td><td class="r">${r.tdsRate}%</td><td class="r b red">${fmt(r.tdsAmt)}</td><td class="r">${fmt(r.baseAmount - r.tdsAmt)}</td><td>${r.source}</td></tr>`).join('');
          const sumRows = secSummary.map(s => `<tr><td colspan="3" class="b">${s.section}</td><td class="r">${fmt(s.totalBase)}</td><td colspan="2"></td><td class="r b red">${fmt(s.totalTds)}</td><td colspan="2">${s.count} entries</td></tr>`).join('');
          const html = `<h1>TDS Payable Report — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy} (Purchases + Expenses with TDS)</div>
<h3>Section-wise Summary</h3><table><thead><tr><th colspan="3">TDS Section</th><th class="r">Total Base</th><th colspan="2"></th><th class="r">TDS Payable</th><th colspan="2">Entries</th></tr></thead><tbody>${sumRows}</tbody><tfoot><tr><td colspan="6" class="b">Grand Total TDS Payable</td><td class="r b red">${fmt(grandTds)}</td><td colspan="2"></td></tr></tfoot></table>
<h3>Transaction Detail</h3><table><thead><tr><th>Date</th><th>Party/Vendor</th><th>Description</th><th class="r">Base Amt</th><th>Section</th><th class="r">Rate</th><th class="r">TDS Amt</th><th class="r">Net Pay</th><th>Source</th></tr></thead><tbody>${rows}</tbody></table>`;
          printHtml(html, `TDS_Payable_${fy}`);
        }

        return (
          <ReportCard
            title={`TDS Payable Report  FY ${fy}  (${tdsPayableRows.length} transactions)`}
            actions={<>
              <Btn k="tdsPayCsv" label="⬇ Excel" onClick={() => {
                const header = ['Date','Party/Vendor','Description','Base Amount','TDS Section','TDS Rate %','TDS Amount','Net Payable','Source'];
                const lines = tdsPayableRows.map(r => [r.date, r.partyOrVendor, r.description, r.baseAmount.toFixed(2), r.tdsSection, r.tdsRate.toFixed(2), r.tdsAmt.toFixed(2), (r.baseAmount - r.tdsAmt).toFixed(2), r.source].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
                downloadCsv([header.join(','), ...lines].join('\n'), `TDS_Payable_${fy}.csv`);
              }} />
              <Btn k="tdsPayPdf" label="🖨 Print" onClick={printTdsPayable} />
            </>}
          >
            {secSummary.length > 0 && (
              <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>Section-wise Summary</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {secSummary.map(s => (
                    <div key={s.section} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.5rem 0.85rem', minWidth: 160 }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9f1239', textTransform: 'uppercase' }}>{s.section}</div>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: '#dc2626' }}>₹{fmtN(s.totalTds)}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>on ₹{fmtN(s.totalBase)} • {s.count} txn</div>
                    </div>
                  ))}
                  <div style={{ background: '#fef2f2', border: '2px solid #ef4444', borderRadius: 8, padding: '0.5rem 0.85rem', minWidth: 160 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9f1239', textTransform: 'uppercase' }}>Grand Total TDS</div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#dc2626' }}>₹{fmtN(grandTds)}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{tdsPayableRows.length} transactions</div>
                  </div>
                </div>
              </div>
            )}
            <table>
              <thead>
                <tr><th>Date</th><th>Party / Vendor</th><th>Description</th><th style={{ textAlign: 'right' }}>Base Amt</th><th>TDS Section</th><th style={{ textAlign: 'right' }}>Rate</th><th style={{ textAlign: 'right' }}>TDS Amt</th><th style={{ textAlign: 'right' }}>Net Payable</th><th>Source</th></tr>
              </thead>
              <tbody>
                {tdsPayableRows.length === 0
                  ? <EmptyRow cols={9} msg="No TDS entries found in purchases or expenses for this period" />
                  : tdsPayableRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.date}</td>
                      <td style={{ fontWeight: 500 }}>{r.partyOrVendor}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>{r.description}</td>
                      <td style={{ textAlign: 'right' }}>{fmtN(r.baseAmount)}</td>
                      <td style={{ fontSize: '0.78rem', fontFamily: 'monospace', color: 'var(--text-2)' }}>{r.tdsSection}</td>
                      <td style={{ textAlign: 'right' }}>{r.tdsRate}%</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{fmtN(r.tdsAmt)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtN(r.baseAmount - r.tdsAmt)}</td>
                      <td><span className={`badge ${r.source === 'purchase' ? 'badge-blue' : 'badge-gray'}`}>{r.source}</span></td>
                    </tr>
                  ))
                }
              </tbody>
              {tdsPayableRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} style={{ fontWeight: 700 }}>Grand Total TDS Payable</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--danger)' }}>{fmtN(grandTds)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </ReportCard>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Stock Movement                                                     */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'operations' && activeReport === 'stockMovement' && <FeatureGate feature="stockReport">
      <ReportCard
        title={<>
          {`Stock Movement Report  FY ${fy}  (${filteredStockRows.length} products)`}
          {stockMismatchCount > 0 && (
            <span style={{ color: 'var(--warning)', marginLeft: '0.6rem', fontSize: '0.82rem' }} title="This report's own computed closing quantity doesn't match the product's live Stock count — usually means its opening stock was never recorded. See the ⚠ marker in the Closing Qty column.">
              ⚠ {stockMismatchCount} don't match live stock
            </span>
          )}
        </>}
        actions={<Btn k="stockpdf" label="🖨 Print" onClick={() => {
          const totVal = filteredStockRows.reduce((s, r) => s + r.closingValue, 0);
          const catLabel = (c?: string) => c === 'raw_material' ? 'Raw Material' : c === 'finished_good' ? 'Finished Good' : '—';
          const html = `<h1>Stock Movement (FIFO) — ${profile?.name ?? ''}</h1><div class="sub">FY ${fy}</div><table><thead><tr><th>Product</th><th>Category</th><th>HSN</th><th>Unit</th><th class="r">Opening</th><th class="r">Purchased</th><th class="r">Produced (Mfg)</th><th class="r">Sold</th><th class="r">Consumed (Mfg)</th><th class="r">Cust.Returns</th><th class="r">Supp.Returns</th><th class="r">Adjustments</th><th class="r">Closing Qty</th><th class="r">Avg.Cost</th><th class="r">Closing Value (FIFO)</th></tr></thead><tbody>${filteredStockRows.map(r => `<tr><td>${r.name}</td><td>${catLabel(r.category)}</td><td>${r.hsnSac || '—'}</td><td>${r.unit}</td><td class="r">${r.opening}</td><td class="r green">${r.purchased}</td><td class="r green">${r.producedMfg || '—'}</td><td class="r red">${r.sold}</td><td class="r red">${r.consumedMfg || '—'}</td><td class="r">${r.custReturned || '—'}</td><td class="r">${r.suppReturned || '—'}</td><td class="r">${r.adjusted !== 0 ? (r.adjusted > 0 ? '+' : '') + r.adjusted : '—'}</td><td class="r b">${r.closing}</td><td class="r">${fmt(r.avgRate)}</td><td class="r b">${fmt(r.closingValue)}</td></tr>`).join('')}</tbody><tfoot><tr><td colspan="14" class="b">Total Closing Stock Value (FIFO)</td><td class="r b">${fmt(totVal)}</td></tr></tfoot></table>`;
          printHtml(html, `Stock Report FIFO ${fy}`);
        }} />}
      >
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          {([
            { v: '', label: 'All' },
            { v: 'raw_material', label: 'Raw Material' },
            { v: 'finished_good', label: 'Finished Good' },
          ] as const).map(t => (
            <button
              key={t.v}
              className={`btn btn-sm ${stockCategoryFilter === t.v ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setStockCategoryFilter(t.v)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th><th>Category</th><th>HSN/SAC</th><th>Unit</th>
              <th style={{ textAlign: 'right' }}>Opening</th>
              <th style={{ textAlign: 'right' }}>Purchased</th>
              <th style={{ textAlign: 'right' }}>Produced (Mfg)</th>
              <th style={{ textAlign: 'right' }}>Sold</th>
              <th style={{ textAlign: 'right' }}>Consumed (Mfg)</th>
              <th style={{ textAlign: 'right' }}>Cust. Returns</th>
              <th style={{ textAlign: 'right' }}>Supp. Returns</th>
              <th style={{ textAlign: 'right' }}>Adjustments</th>
              <th style={{ textAlign: 'right' }}>Closing Qty</th>
              <th style={{ textAlign: 'right' }}>Avg. Cost</th>
              <th style={{ textAlign: 'right' }}>Closing Value (FIFO)</th>
            </tr>
          </thead>
          <tbody>
            {filteredStockRows.length === 0
              ? <EmptyRow cols={15} msg="No stock products found" />
              : filteredStockRows.map(r => {
                const isLow = r.closing <= 0 || r.closing <= (products.find(p => p.id === r.id)?.reorderLevel ?? 0);
                const mismatched = isStockMismatched(r);
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>{r.category
                      ? <span className={`badge ${r.category === 'raw_material' ? 'badge-orange' : 'badge-blue'}`}>{r.category === 'raw_material' ? 'Raw Material' : 'Finished Good'}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.hsnSac || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{r.unit}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{r.opening}</td>
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: r.purchased > 0 ? 600 : 400 }}>{r.purchased > 0 ? r.purchased : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: r.producedMfg > 0 ? 600 : 400 }}>{r.producedMfg > 0 ? r.producedMfg : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: r.sold > 0 ? 600 : 400 }}>{r.sold > 0 ? r.sold : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: r.consumedMfg > 0 ? 600 : 400 }}>{r.consumedMfg > 0 ? r.consumedMfg : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--primary)', fontSize: '0.85rem' }}>{r.custReturned > 0 ? r.custReturned : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--warning)', fontSize: '0.85rem' }}>{r.suppReturned > 0 ? r.suppReturned : '—'}</td>
                    <td style={{ textAlign: 'right', fontSize: '0.85rem', color: r.adjusted > 0 ? 'var(--success)' : r.adjusted < 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                      {r.adjusted !== 0 ? (r.adjusted > 0 ? '+' : '') + r.adjusted : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: r.closing <= 0 ? 'var(--danger)' : isLow ? 'var(--warning)' : 'var(--text-1)' }}>
                      {r.closing}
                      {r.closing <= 0 ? <span style={{ fontSize: '0.68rem', marginLeft: 4 }}>OUT</span> : isLow ? <span style={{ fontSize: '0.68rem', marginLeft: 4 }}>LOW</span> : null}
                      {mismatched && (
                        <span
                          style={{ fontSize: '0.68rem', marginLeft: 4, color: 'var(--warning)' }}
                          title={`This report computes ${r.closing}, but the product's live Stock count is ${productStockById.get(r.id)} — usually means Opening Stock was never recorded for it. See "Backfill Opening Stock" on the Products page.`}
                        >⚠ MISMATCH</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: '0.85rem' }}>{r.avgRate > 0 ? fmtN(r.avgRate) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{fmtN(r.closingValue)}</td>
                  </tr>
                );
              })
            }
          </tbody>
          {filteredStockRows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={14} style={{ fontWeight: 700 }}>Total Closing Stock Value (FIFO)</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(filteredStockRows.reduce((s, r) => s + r.closingValue, 0))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </ReportCard>
      </FeatureGate>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Party Ledger                                                       */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Kept on the same "stockReport" plan feature it was already gated by
          (pre-existing behaviour — Party Ledger and Stock Movement used to
          share one <FeatureGate>, now split so each can live in its own
          tab); not a new gating decision. */}
      {activeTab === 'moneyInOut' && activeReport === 'partyLedger' && <FeatureGate feature="stockReport">
      <ReportCard
        title="Party Ledger"
        actions={partyId && ledgerData.length > 0 ? <Btn k="partyledger" label="🖨 Print" onClick={printPartyLedger} /> : undefined}
      >
        <div style={{ padding: '0.75rem 1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            Select Party
            <select value={partyId} onChange={e => setPartyId(e.target.value)} style={{ width: 'auto', minWidth: 220 }}>
              <option value="">— Choose a party —</option>
              {[...parties].sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
              ))}
              {invoicePartyNames.length > 0 && (
                <optgroup label="From invoices (not yet a party record)">
                  {invoicePartyNames.map(n => (
                    <option key={`name:${n}`} value={`name:${n}`}>{n}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          {ledgerData.length > 0 && (
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {ledgerData.length} entries · Closing: <strong style={{ color: (ledgerData[ledgerData.length - 1]?.balance ?? 0) >= 0 ? 'var(--danger)' : 'var(--success)' }}>
                {fmtN(Math.abs(ledgerData[ledgerData.length - 1]?.balance ?? 0))} {(ledgerData[ledgerData.length - 1]?.balance ?? 0) >= 0 ? 'Dr' : 'Cr'}
              </strong>
            </span>
          )}
        </div>
        {!partyId
          ? <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Select a party above to view their ledger</div>
          : ledgerLoading
            ? <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading ledger…</div>
            : (
              <table>
                <thead><tr><th>Date</th><th>Type</th><th>Description</th><th style={{ textAlign: 'right' }}>Debit (Dr)</th><th style={{ textAlign: 'right' }}>Credit (Cr)</th><th style={{ textAlign: 'right' }}>Balance</th></tr></thead>
                <tbody>
                  {ledgerData.length === 0
                    ? <EmptyRow cols={6} msg="No transactions found for this party" />
                    : ledgerData.map((e, idx) => (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{e.date}</td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {e.type === 'invoice' ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Sale</span>
                            : e.type === 'credit_note' ? <span style={{ color: 'var(--success)' }}>Credit Note</span>
                            : e.type === 'purchase' ? <span style={{ color: '#7c3aed', fontWeight: 600 }}>Purchase</span>
                            : e.type === 'debit_note' ? <span style={{ color: '#d97706' }}>Debit Note</span>
                            : e.type === 'purchase_payment' ? <span style={{ color: 'var(--success)' }}>Pmt (Purchase)</span>
                            : e.type === 'payment' ? <span style={{ color: 'var(--success)' }}>Payment</span>
                            : e.type === 'tds' ? <span style={{ color: '#0891b2' }}>TDS</span>
                            : <span style={{ textTransform: 'capitalize' }}>{e.type}</span>}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>{e.description}{e.invoiceNumber ? ` #${e.invoiceNumber}` : ''}</td>
                        <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: e.debit > 0 ? 600 : 400 }}>{e.debit > 0 ? fmtN(e.debit) : '—'}</td>
                        <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: e.credit > 0 ? 600 : 400 }}>{e.credit > 0 ? fmtN(e.credit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: e.balance > 0 ? 'var(--danger)' : e.balance < 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                          {fmtN(Math.abs(e.balance))} {e.balance > 0 ? 'Dr' : e.balance < 0 ? 'Cr' : ''}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
                {ledgerData.length > 0 && (() => {
                  const closing = ledgerData[ledgerData.length - 1]?.balance ?? 0;
                  return (
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ fontWeight: 700 }}>Closing Balance</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: closing >= 0 ? 'var(--danger)' : 'var(--success)' }}>
                          {fmtN(Math.abs(closing))} {closing >= 0 ? 'Dr' : 'Cr'}
                        </td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            )
        }
      </ReportCard>
      </FeatureGate>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Quotation Tracker                                                  */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'salesPurchases' && activeReport === 'quotationTracker' && <ReportCard
        title={`Quotation Tracker  FY ${fy}  (${quotations.length} quotes)`}
        actions={<Btn k="quotcsv" label="⬇ CSV" onClick={() => {
          if (!guardExport()) return;
          const header = ['Quote #','Date','Party','GSTIN','Total','Status','Converted?'];
          const lines = quotations.map(q => { const converted = invoices.some(i => i.docType !== 'quotation' && i.referenceNumber === q.invoiceNumber); return [q.invoiceNumber ?? '', q.date, q.partyName, q.partyGstin ?? '', q.total.toFixed(2), q.status, converted ? 'Yes' : 'No'].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','); });
          downloadCsv([header.join(','), ...lines].join('\n'), `Quotations_${fy}.csv`);
        }} />}
      >
        {quotations.length === 0
          ? <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No quotations in FY {fy}</div>
          : (
            <>
              <div style={{ display: 'flex', gap: '1.5rem', padding: '0.65rem 1.25rem', background: 'var(--bg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                {[
                  { label: 'Total Value', val: fmtN(quotations.reduce((s, q) => s + q.total, 0)), color: 'var(--text-1)' },
                  { label: 'Converted', val: String(quotations.filter(q => invoices.some(i => i.docType !== 'quotation' && i.referenceNumber === q.invoiceNumber)).length), color: 'var(--success)' },
                  { label: 'Pending', val: String(quotations.filter(q => !invoices.some(i => i.docType !== 'quotation' && i.referenceNumber === q.invoiceNumber)).length), color: 'var(--warning)' },
                  { label: 'Conversion Rate', val: `${quotations.length ? Math.round(quotations.filter(q => invoices.some(i => i.docType !== 'quotation' && i.referenceNumber === q.invoiceNumber)).length / quotations.length * 100) : 0}%`, color: 'var(--primary)' },
                ].map(k => (
                  <span key={k.label} style={{ fontSize: '0.82rem' }}>
                    {k.label}: <strong style={{ color: k.color }}>{k.val}</strong>
                  </span>
                ))}
              </div>
              <table>
                <thead><tr><th>Quote #</th><th>Date</th><th>Party</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th><th>Converted?</th></tr></thead>
                <tbody>
                  {quotations.sort((a, b) => b.date.localeCompare(a.date)).map(q => {
                    const converted = invoices.some(i => i.docType !== 'quotation' && i.referenceNumber === q.invoiceNumber);
                    return (
                      <tr key={q.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{q.invoiceNumber}</td>
                        <td>{q.date}</td>
                        <td>{q.partyName}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtN(q.total)}</td>
                        <td><StatusBadge status={q.status} /></td>
                        <td>
                          <span className={`badge ${converted ? 'badge-paid' : 'badge-unpaid'}`} style={{ fontSize: '0.72rem' }}>
                            {converted ? '✓ Converted' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )
        }
      </ReportCard>}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* GSTR-2A Reconciliation                                             */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'gst' && activeReport === 'gstr2a' && <ReportCard
        title="GSTR-2A Reconciliation"
        actions={
          <>
            {gstr2aRows && <button className="btn btn-ghost btn-sm" onClick={() => { setGstr2aRows(null); setGstr2aError(null); setGstr2aSyncState('idle'); setGstr2aSyncMsg(null); }} style={{ fontSize: '0.78rem' }}>Clear</button>}
            {gstr2aSyncMsg && <span style={{ fontSize: '0.78rem', color: 'var(--success)' }}>{gstr2aSyncMsg}</span>}
            {gstr2aSyncState === 'idle' && (
              <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem' }} onClick={async () => {
                const gstin = profile?.gstin;
                if (!gstin) { alert('Configure GSTIN in Company Profile first'); return; }
                try {
                  const res = await apiClient.post<{ txnId: string; note: string }>('/gstr2a/request-otp', { gstin });
                  setGstr2aSyncTxnId(res.txnId);
                  setGstr2aSyncState('otp_sent');
                  setGstr2aSyncMsg(res.note);
                } catch (e) { alert(e instanceof Error ? e.message : 'OTP request failed'); }
              }}>Sync from GST Portal</button>
            )}
            {gstr2aSyncState === 'otp_sent' && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="text" placeholder="Enter OTP" value={gstr2aSyncOtp} onChange={e => setGstr2aSyncOtp(e.target.value)}
                  style={{ width: 120, fontSize: '0.8rem', padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)' }} />
                <button className="btn btn-primary btn-sm" style={{ fontSize: '0.78rem' }} onClick={async () => {
                  setGstr2aSyncState('syncing');
                  try {
                    const period = `${from.slice(5, 7)}${from.slice(0, 4)}`;
                    const res = await apiClient.post<{ entryCount: number; note: string; returnPeriod: string }>('/gstr2a/sync', { otp: gstr2aSyncOtp, txnId: gstr2aSyncTxnId, returnPeriod: period });
                    setGstr2aSyncMsg(`Synced ${res.entryCount} entries for ${res.returnPeriod}. ${res.note}`);
                  } catch (e) { alert(e instanceof Error ? e.message : 'Sync failed'); }
                  finally { setGstr2aSyncState('idle'); setGstr2aSyncOtp(''); }
                }} disabled={!gstr2aSyncOtp}>Submit OTP</button>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem' }} onClick={() => setGstr2aSyncState('idle')}>Cancel</button>
              </div>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => gstr2aRef.current?.click()} style={{ fontSize: '0.78rem' }}>Upload GSTR-2A JSON</button>
            <input ref={gstr2aRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleGstr2aUpload} />
          </>
        }
      >
        {gstr2aError && <div className="error-box" style={{ margin: '1rem 1.25rem' }}>{gstr2aError}</div>}
        {!gstr2aRows && !gstr2aError && (
          <div style={{ padding: '1.5rem 1.25rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Download GSTR-2A JSON from the GST portal (Returns → GSTR-2A → Download JSON), then upload it here to compare against your purchase invoices.
          </div>
        )}
        {gstr2aRows && (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
              {(['matched', 'in_2a_only', 'in_local_only'] as const).map(s => {
                const meta = { matched: { label: 'Matched', color: 'var(--success)', bg: '#f0fdf4' }, in_2a_only: { label: 'In GSTR-2A only', color: 'var(--danger)', bg: '#fef2f2' }, in_local_only: { label: 'Local only (not in 2A)', color: 'var(--warning)', bg: '#fffbeb' } }[s];
                const count = gstr2aRows.filter(r => r.matchStatus === s).length;
                return <div key={s} style={{ background: meta.bg, border: `1px solid ${meta.color}40`, borderRadius: 6, padding: '0.35rem 0.75rem', fontSize: '0.8rem', color: meta.color, fontWeight: 600 }}>{meta.label}: {count}</div>;
              })}
            </div>
            <table>
              <thead><tr><th>Status</th><th>Supplier GSTIN</th><th>Supplier</th><th>Inv #</th><th>Date</th><th style={{ textAlign: 'right' }}>Taxable</th><th style={{ textAlign: 'right' }}>Tax (2A)</th><th style={{ textAlign: 'right' }}>Tax Diff</th></tr></thead>
              <tbody>
                {gstr2aRows.map((r, i) => {
                  const statusMeta = { matched: { label: '✓ Matched', cls: 'badge-paid' }, in_2a_only: { label: '! In 2A only', cls: 'badge-overdue' }, in_local_only: { label: '~ Local only', cls: 'badge-unpaid' } }[r.matchStatus];
                  return (
                    <tr key={i}>
                      <td><span className={`badge ${statusMeta.cls}`} style={{ fontSize: '0.72rem' }}>{statusMeta.label}</span></td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.supplierGstin}</td>
                      <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.supplierName}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.invoiceNumber}</td>
                      <td>{r.invoiceDate}</td>
                      <td style={{ textAlign: 'right' }}>{fmtN(r.taxableValue)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtN(r.totalTax)}</td>
                      <td style={{ textAlign: 'right', color: r.taxDiff ? (r.taxDiff > 0 ? 'var(--danger)' : 'var(--success)') : 'var(--text-muted)', fontWeight: r.taxDiff ? 600 : 400 }}>
                        {r.taxDiff != null ? (r.taxDiff > 0 ? '+' : '') + fmtN(r.taxDiff) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </ReportCard>}

      {activeTab === 'operations' && activeReport === 'productionCostVariance' && <FeatureGate feature="manufacturingModule" requiredPlan="Gold" upgradeMessage="Production cost variance requires a Gold plan.">
      <ReportCard title="Production Cost Variance" actions={null}>
        {(() => {
          const rows: CostVarianceDto[] = costVariance ?? [];
          if (rows.length === 0) {
            return <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No completed manufacturing orders yet.</p>;
          }
          return (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Order</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Product</th>
                    <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Qty</th>
                    <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Planned/unit</th>
                    <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Actual/unit</th>
                    <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Variance</th>
                    <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--muted)', fontWeight: 500 }}>Var %</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isFav = r.varianceAmount > 0;
                    const varColor = Math.abs(r.variancePct) < 1 ? 'var(--text)' : isFav ? '#dc2626' : '#16a34a';
                    return (
                      <tr key={r.orderId} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{r.orderNumber}</td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{r.product}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{r.completedQty.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>₹{r.plannedCostPerUnit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>₹{r.actualCostPerUnit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: varColor, fontWeight: 500 }}>
                          {r.varianceAmount >= 0 ? '+' : ''}₹{r.varianceAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: varColor, fontWeight: 500 }}>
                          {r.variancePct >= 0 ? '+' : ''}{r.variancePct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                Green = actual cost below planned (favorable). Red = actual cost over planned (unfavorable).
              </p>
            </div>
          );
        })()}
      </ReportCard>
      </FeatureGate>}
    </div>
  );
}
